import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import socketConnection from './services/socketConnection';
import VoucherDiagnosticSidebar from './VoucherDiagnosticSidebar';
import VoucherSearchModal from './VoucherSearchModal';
import VoucherLatestModal from './VoucherLatestModal';
import {
  Box, Group, Stack, Text, Avatar, Badge, Title,
  TextInput, ActionIcon, Tooltip, Button, ScrollArea,
  Divider, Menu, Modal, Paper, Tabs, UnstyledButton,
  Indicator, Flex, Drawer, Progress, RingProgress, SimpleGrid,
  SegmentedControl, Select, Table, Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconMessage, IconPhone, IconPhoneOff, IconMail, IconUsers, IconUser,
  IconSearch, IconPlus, IconSend, IconMoodSmile, IconPaperclip,
  IconPhoto, IconMicrophone, IconCamera, IconDots, IconVolume3,
  IconPin, IconTrash, IconBan, IconId, IconBell, IconX, IconCheck,
  IconPlayerStop, IconVideo, IconChevronDown, IconChevronUp,
  IconSettings, IconBolt, IconLogout, IconWifi, IconArrowUpRight, IconUserPlus,
  IconAlertTriangle, IconChartBar, IconClock, IconTicket, IconHistory, IconFileText,
} from '@tabler/icons-react';
import classes from './AdminDashboard.module.css';

// SLA Configuration
const SLA_THRESHOLD_MINUTES = 30; // SLA threshold for resolution (30 minutes)

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3004/api';

// Use shared socket connection
const socket = socketConnection();

/* ── Helpers ── */
const COLORS   = ['violet','blue','pink','teal','orange','green','yellow','grape'];
const avatarColor  = (n) => { let h=0; for(let c of (n||'')) h+=c.charCodeAt(0); return COLORS[h%COLORS.length]; };
const getInitial   = (n) => (n||'?').charAt(0).toUpperCase();
const formatTime   = (ts) => new Date(ts).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'});
const timeAgo      = (ts) => {
  const d=Math.floor((Date.now()-new Date(ts))/1000);
  if(d<60) return 'Just now';
  if(d<3600) return `${Math.floor(d/60)}m ago`;
  if(d<86400) return `${Math.floor(d/3600)}h ago`;
  return 'Yesterday';
};

// Calculate time since chat started (in minutes)
const getChatAgeMinutes = (joinedAt) => {
  if (!joinedAt) return 0;
  return Math.floor((Date.now() - new Date(joinedAt)) / 60000);
};

// Format minutes to readable time
const formatResolutionTime = (minutes) => {
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Format seconds to MM:SS
const formatCallDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Check if SLA is breached
const isSLABreached = (joinedAt, resolved) => {
  if (resolved) return false; // Already resolved, check was done at resolution time
  return getChatAgeMinutes(joinedAt) > SLA_THRESHOLD_MINUTES;
};
const now = (m=0) => new Date(Date.now()-m*60000).toISOString();

/* ── Mock Data ── */
// Real data comes from socket.io connections

const EMOJIS = ['😊','😂','❤️','👍','🙏','😍','🎉','🔥','😢','💯','👏','🤔','😎','🫡','💬'];

/* ── Canned Responses ── */
const CANNED = [
  { category:'Greetings', color:'blue', items:[
    { label:'Welcome',       text:'Hello! Welcome to our support. How can I assist you today?'           },
    { label:'Good morning',  text:'Good morning! Thanks for reaching out. How can I help?'              },
    { label:'Returning user',text:'Welcome back! Great to hear from you again. How can I help?'         },
  ]},
  { category:'Common Queries', color:'violet', items:[
    { label:'Ask order #',   text:'Could you please provide your order number so I can look into this?' },
    { label:'Ask email',     text:'May I have the email address associated with your account?'           },
    { label:'Checking now',  text:'Let me check that for you right away — please give me a moment.'     },
    { label:'Processing',    text:'I have submitted your request. It will be processed within 3–5 business days.' },
  ]},
  { category:'Closings', color:'violet', items:[
    { label:'Resolved',      text:'I am glad we could resolve this for you. Is there anything else I can help with?' },
    { label:'Thank you',     text:'Thank you for contacting support. Have a wonderful day!'              },
    { label:'Follow up',     text:'Feel free to reach out anytime. We are always here to help!'         },
  ]},
  { category:'Handoffs', color:'orange', items:[
    { label:'Transfer',      text:'I will be transferring you to a specialist who can better assist you with this matter.' },
    { label:'Escalate',      text:'I am escalating this to our senior team. You will receive an update via email within 24 hours.' },
    { label:'Callback',      text:'Our specialist will call you back at the number on file within 2 hours.' },
  ]},
];

const AGENT = {
  name:       'Support Agent',
  role:       'Support Agent',
  email:      'support@helpdesk.io',
  phone:      '',
  location:   'New York, USA',
  timezone:   'UTC−5 (EST)',
  joined:     'January 2022',
  status:     'online',
  avatar:     'AM',
  stats: {
    totalChats:    1248,
    resolvedToday:   14,
    avgResponseTime: '2m 18s',
    rating:          4.8,
    satisfaction:    94,
  },
  skills: ['Billing','Technical','Account','Refunds','Onboarding'],
};
const GROUP_ROOMS  = [{id:'g1',name:'Product Team',preview:'Great work everyone!',time:'11:30am'},{id:'g2',name:'Support Channel',preview:'New ticket assigned',time:'10:15am'},{id:'g3',name:'Marketing',preview:'Campaign is live!',time:'9:00am'}];
const PUBLIC_ROOMS = [{id:'p1',name:'#general',preview:'Welcome to general chat',time:'12:00pm'},{id:'p2',name:'#announcements',preview:'Server maintenance tonight',time:'8:00am'},{id:'p3',name:'#feedback',preview:'Love the new UI update!',time:'Yesterday'}];

/* ──────────────────────────────────────────
   COMPONENT
────────────────────────────────────────── */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [rooms,         setRooms]         = useState({});
  const [queue,         setQueue]         = useState([]);
  const [selectedRoom,  setSelectedRoom]  = useState(null);
  const [reply,         setReply]         = useState('');
  const [search,        setSearch]        = useState('');
  const [notifs,        setNotifs]        = useState([]);
  const [historicalSessions, setHistoricalSessions] = useState([]);
  const [activeTab,     setActiveTab]     = useState('Direct');
  const [showEmoji,     setShowEmoji]     = useState(false);
  const [callModal,     setCallModal]     = useState(null);
  const [callStatus,    setCallStatus]    = useState(null); // 'calling'|'connected'|'ended'
  const [callTargetRoomId, setCallTargetRoomId] = useState(null); // Target client's socket ID for calls
  const [incomingCall, setIncomingCall]  = useState(null); // { callType, from }
  const [callStartTime, setCallStartTime] = useState(null); // When call was connected
  const [callDuration, setCallDuration] = useState(0); // Call duration in seconds
  const [localStream,   setLocalStream]   = useState(null);
  const [remoteStream,  setRemoteStream]  = useState(null);
  const peerConnection = useRef(null);
  const [addedContacts, setAddedContacts] = useState([]);
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [showQueue,     setShowQueue]     = useState(true);
  const [asideOpen,       setAsideOpen]       = useState(true);
  const [profileOpen,     setProfileOpen]     = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false); // Edit profile modal
  const [editProfileForm, setEditProfileForm] = useState({ fullName: '', phone: '', department: '' });
  const [agentStatus, setAgentStatus] = useState('online'); // 'online' or 'away'
  const [cannedOpen,      setCannedOpen]      = useState(false);
  const [sneakPeeks,      setSneakPeeks]      = useState({}); // { roomId: text }
  const [statusFilter,    setStatusFilter]    = useState('all'); // 'all'|'open'|'resolved'
  const [clearHistoryModalOpen, setClearHistoryModalOpen] = useState(false); // Modal for confirming clear history
  const [currentAgent, setCurrentAgent] = useState(null); // Current logged in agent
  const [voucherSidebarOpen, setVoucherSidebarOpen] = useState(false); // Voucher search modal
  const [notesModalOpen, setNotesModalOpen] = useState(false); // Notes modal
  const [editingNotes, setEditingNotes] = useState(''); // Notes being edited
  const [escalateModalOpen, setEscalateModalOpen] = useState(false); // Escalation modal
  const [escalateNotes, setEscalateNotes] = useState(''); // Escalation summary/notes
  const [escalateError, setEscalateError] = useState(''); // Validation error
  
  // Transfer to manager state
  const [transferModalOpen, setTransferModalOpen] = useState(false); // Transfer modal
  const [managers, setManagers] = useState([]); // List of managers
  const [selectedManager, setSelectedManager] = useState(null); // Selected manager for transfer
  const [escalatedChats, setEscalatedChats] = useState({}); // { roomId: { escalated: boolean, escalatedTo: string, escalatedAt: date } }
  
  // Issue tracking stats
  const [issueStats, setIssueStats] = useState({ resolved: 0, unresolved: 0, escalated: 0 });
  
  // Time tracking for resolution (SLA)
  const [resolutionTimes, setResolutionTimes] = useState([]); // Array of resolution times in minutes
  
  // SLA Stats
  const [slaStats, setSlaStats] = useState({
    avgResolutionTime: 0,
    slaCompliance: 100,
    withinSLA: 0,
    breachedSLA: 0,
    openAge: 0
  });
  
  // Calculate issue stats when rooms change
  useEffect(() => {
    const roomsArray = Object.values(rooms);
    const resolved = roomsArray.filter(r => r.resolved).length;
    const escalated = roomsArray.filter(r => r.escalated).length;
    const unresolved = roomsArray.filter(r => !r.resolved).length;
    setIssueStats({ resolved, unresolved, escalated });
    
    // Calculate SLA stats
    const resolvedRooms = roomsArray.filter(r => r.resolved && r.resolvedAt && r.joinedAt);
    const withinSLA = resolvedRooms.filter(r => {
      const resolutionTime = (new Date(r.resolvedAt) - new Date(r.joinedAt)) / 60000;
      return resolutionTime <= SLA_THRESHOLD_MINUTES;
    }).length;
    const breachedSLA = resolvedRooms.length - withinSLA;
    
    // Calculate average resolution time from resolved chats
    let totalResolutionTime = 0;
    resolvedRooms.forEach(r => {
      totalResolutionTime += (new Date(r.resolvedAt) - new Date(r.joinedAt)) / 60000;
    });
    const avgResolutionTime = resolvedRooms.length > 0 ? Math.round(totalResolutionTime / resolvedRooms.length) : 0;
    
    // SLA compliance percentage
    const slaCompliance = resolvedRooms.length > 0 ? Math.round((withinSLA / resolvedRooms.length) * 100) : 100;
    
    // Average age of open chats
    const openRooms = roomsArray.filter(r => !r.resolved);
    let totalOpenAge = 0;
    openRooms.forEach(r => {
      totalOpenAge += getChatAgeMinutes(r.joinedAt);
    });
    const openAge = openRooms.length > 0 ? Math.round(totalOpenAge / openRooms.length) : 0;
    
    setSlaStats({
      avgResolutionTime,
      slaCompliance,
      withinSLA,
      breachedSLA,
      openAge
    });
  }, [rooms]);

  // Call duration timer
  useEffect(() => {
    let interval;
    if (callStatus === 'connected' && callStartTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - callStartTime) / 1000);
        setCallDuration(elapsed);
      }, 1000);
    } else if (callStatus !== 'connected') {
      setCallDuration(0);
      setCallStartTime(null);
    }
    return () => clearInterval(interval);
  }, [callStatus, callStartTime]);

  // Fetch transactions when Calls tab is active
  useEffect(() => {
    if (activeTab === 'Calls') {
      fetchTransactions();
    }
  }, [activeTab, transactionsPage, transactionSearch]);

  const [voucherSearchEmail, setVoucherSearchEmail] = useState(''); // Voucher search email
  const [latestVoucherOpened, setLatestVoucherOpened] = useState(false); // Latest voucher modal
  const messagesEnd = useRef(null);
  const fileInput   = useRef(null);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('helpdeskToken');
    const user = localStorage.getItem('helpdeskUser');
    
    if (!token || !user) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(user);
    // Only allow helpdesk_agent to access the helpdesk interface
    if (userData.role !== 'helpdesk_agent') {
      navigate('/login');
      return;
    }
    
    // Set current agent data for profile display
    setCurrentAgent({
      name: userData.fullName || userData.username || 'Support Agent',
      email: userData.email || 'support@helpdesk.io',
      phone: userData.phone || '',
      location: userData.location || 'Not set',
      timezone: 'UTC+8 (Asia/Shanghai)',
      role: userData.role === 'super_admin' ? 'Super Admin' : userData.role === 'manager' ? 'Manager' : userData.role === 'supervisor' ? 'Supervisor' : userData.department || 'Help Desk Agent',
      department: userData.department || 'Support',
      joined: userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : new Date().toLocaleDateString(),
      status: userData.status === 'active' ? 'online' : userData.status === 'away' ? 'away' : 'offline',
      avatar: (userData.fullName || userData.username || 'SA').charAt(0).toUpperCase(),
      skills: userData.skills || ['General Support'],
      stats: {
        totalChats: userData.stats?.totalChats || 0,
        resolvedToday: userData.stats?.resolvedToday || 0,
        avgResponseTime: userData.stats?.avgResponseTime || '0m 0s',
        rating: userData.stats?.rating || 0,
        satisfaction: userData.stats?.satisfaction || 0,
      }
    });
    
    // Set agent status based on user data
    setAgentStatus(userData.status === 'active' ? 'online' : userData.status === 'away' ? 'away' : 'offline');
  }, [navigate]);

  const toast = useCallback((msg, color='blue') =>
    notifications.show({ message:msg, color, autoClose:2500 }), []);

  const handleLogout = () => {
    // Emit status change to offline before disconnecting
    const userData = JSON.parse(localStorage.getItem('helpdeskUser') || '{}');
    if (userData._id) {
      socket.emit('agent:status_change', { userId: userData._id, status: 'offline' }, () => {
        // Callback after server acknowledges — clean up and navigate
        localStorage.removeItem('helpdeskToken');
        localStorage.removeItem('helpdeskUser');
        navigate('/login');
      });
      // Also call the logout API to update DB status to inactive
      const token = localStorage.getItem('helpdeskToken');
      fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
      // Fallback: if server doesn't acknowledge within 500ms, navigate anyway
      setTimeout(() => {
        localStorage.removeItem('helpdeskToken');
        localStorage.removeItem('helpdeskUser');
        navigate('/login');
      }, 500);
    } else {
      localStorage.removeItem('helpdeskToken');
      localStorage.removeItem('helpdeskUser');
      navigate('/login');
    }
  };

  const handleClearHistory = () => {
    setClearHistoryModalOpen(true);
  };

  const confirmClearHistory = () => {
    socket.emit('admin:clear_history');
  };

  const handleOpenEditProfile = () => {
    // Pre-fill form with current agent data
    setEditProfileForm({
      fullName: currentAgent?.name || '',
      phone: currentAgent?.phone || '',
      department: currentAgent?.department || ''
    });
    setEditProfileOpen(true);
  };

  const handleEditProfile = async () => {
    try {
      const token = localStorage.getItem('helpdeskToken');
      const response = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editProfileForm)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = await response.json();
      
      // Update local state and localStorage
      setCurrentAgent({
        ...currentAgent,
        name: updatedUser.fullName,
        phone: editProfileForm.phone,
        department: updatedUser.department,
        avatar: (updatedUser.fullName || 'SA').charAt(0).toUpperCase()
      });
      
      // Update localStorage
      const userData = JSON.parse(localStorage.getItem('helpdeskUser') || '{}');
      localStorage.setItem('helpdeskUser', JSON.stringify({
        ...userData,
        fullName: updatedUser.fullName,
        phone: editProfileForm.phone,
        department: updatedUser.department
      }));
      
      setEditProfileOpen(false);
      setProfileOpen(false);
      toast('Profile updated successfully', 'green');
    } catch (err) {
      toast(err.message, 'red');
    }
  };

  const handleToggleStatus = async () => {
    // Determine the new status to send to API
    const currentIsOnline = agentStatus === 'online';
    const newApiStatus = currentIsOnline ? 'away' : 'active'; // 'active' = online in our system
    const newUiStatus = currentIsOnline ? 'away' : 'online';
    
    try {
      const token = localStorage.getItem('helpdeskToken');
      const response = await fetch(`${API_URL}/auth/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newApiStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }

      // Update local state
      setAgentStatus(newUiStatus);
      
      // Update localStorage to persist status
      const userData = JSON.parse(localStorage.getItem('helpdeskUser') || '{}');
      userData.status = newApiStatus;
      localStorage.setItem('helpdeskUser', JSON.stringify(userData));
      
      // Update current agent status
      if (currentAgent) {
        setCurrentAgent({ ...currentAgent, status: newUiStatus });
      }
      
      // Emit status change via socket for real-time updates
      socket.emit('agent:status_change', { userId: userData._id, status: newApiStatus });
      
      toast(newUiStatus === 'online' ? 'You are now online' : 'You are now away', newUiStatus === 'online' ? 'green' : 'orange');
    } catch (err) {
      toast(err.message, 'red');
    }
  };

  // Fetch managers for transfer functionality
  const fetchManagers = async () => {
    try {
      const token = localStorage.getItem('helpdeskToken');
      const response = await fetch(`${API_URL}/users?role=manager`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setManagers(data.users || data || []);
      }
    } catch (err) {
      console.error('Failed to fetch managers:', err);
    }
  };

  // Fetch call transactions
  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const token = localStorage.getItem('helpdeskToken');
      const response = await fetch(
        `${API_URL}/call-transactions?page=${transactionsPage}&limit=10&search=${transactionSearch}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions);
        setTransactionsTotal(data.pagination.total);
      }
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      toast('Error fetching call transactions', 'red');
    } finally {
      setTransactionsLoading(false);
    }
  };


  // Handle transfer to manager
  const handleTransferToManager = (manager) => {
    if (!selectedRoom || !manager) return;
    
    const userData = JSON.parse(localStorage.getItem('helpdeskUser') || '{}');
    
    // Emit transfer event
    socket.emit('admin:transfer', {
      roomId: selectedRoom,
      toManagerId: manager._id,
      toManagerName: manager.fullName || manager.username,
      fromAgentId: userData._id,
      fromAgentName: userData.fullName || userData.username
    }, (response) => {
      if (response.success) {
        toast(`Chat transferred to ${manager.fullName || manager.username}`, 'green');
        // Close the chat after transfer
        setSelectedRoom(null);
        setTransferModalOpen(false);
      } else {
        toast(response.error || 'Failed to transfer chat', 'red');
      }
    });
  };

  // Handle escalate to monitor — requires notes/summary
  const handleEscalateToMonitor = (notes) => {
    if (!selectedRoom) return;

    const userData = JSON.parse(localStorage.getItem('helpdeskUser') || '{}');
    const escalationData = {
      roomId: selectedRoom,
      escalated: true,
      escalatedTo: 'monitor',
      escalatedBy: userData.fullName || userData.username,
      escalatedAt: new Date().toISOString()
    };

    // Save the notes to the room state and DB
    socket.emit('admin:save_notes', { roomId: selectedRoom, notes });

    // Emit escalation event to transfer chat to manager queue (include notes directly to avoid race condition)
    const roomData = rooms[selectedRoom] || {};
    socket.emit('admin:escalate', {
      ...escalationData,
      fromAgentId: userData.id || userData._id,
      fromAgentName: userData.fullName || userData.username,
      clientName: roomData.userName || 'Unknown Client',
      clientEmail: roomData.email || '',
      notes
    }, (response) => {
      if (response.success) {
        setEscalatedChats(prev => ({...prev, [selectedRoom]: escalationData}));
        setRooms(prev => ({...prev, [selectedRoom]: {...prev[selectedRoom], ...escalationData, notes}}));
        // Clear selected room since it's removed from inbox
        setSelectedRoom(null);
        toast('Chat escalated to manager queue', 'orange');
      } else {
        toast(response.error || 'Failed to escalate chat', 'red');
      }
    });
  };

  // Handle remove escalation
  const handleRemoveEscalation = () => {
    if (!selectedRoom) return;

    socket.emit('admin:escalate', {
      roomId: selectedRoom,
      escalated: false,
      escalatedTo: null,
      escalatedAt: null
    }, (response) => {
      if (response.success) {
        setEscalatedChats(prev => {
          const updated = {...prev};
          delete updated[selectedRoom];
          return updated;
        });
        setRooms(prev => {
          const updated = {...prev[selectedRoom]};
          delete updated.escalated;
          delete updated.escalatedTo;
          delete updated.escalatedAt;
          return {...prev, [selectedRoom]: updated};
        });
        toast('Escalation removed', 'blue');
      } else {
        toast(response.error || 'Failed to remove escalation', 'red');
      }
    });
  };

  /* Socket listeners */
  useEffect(() => {
    // Send agent info when joining so server can track status
    const userData = JSON.parse(localStorage.getItem('helpdeskUser') || '{}');
    
    // Function to join admin room and emit events
    const joinAdminRoom = () => {
      socket.emit('admin:join', {
        userId: userData._id,
        username: userData.username,
        fullName: userData.fullName,
        role: userData.role,
        status: 'active'
      });
      
      // Also emit agent:status_change to update status in real-time to all listeners
      // This ensures SuperAdmin sees the agent as online immediately
      if (userData._id) {
        socket.emit('agent:status_change', { userId: userData._id, status: 'active' });
      }

      // Fetch existing chat sessions from database to persist messages on page reload
      fetch(`${API_URL}/chat-sessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('helpdeskToken')}`
        }
      })
      .then(res => res.json())
      .then(sessions => {
        if (Array.isArray(sessions)) {
          // Separate queued sessions from active/closed sessions
          const queuedSessions = [];
          const activeRooms = {};
          
          sessions.forEach(session => {
            if (session.status === 'queued') {
              // Add to queue
              queuedSessions.push({
                socketId: session.roomId,
                userName: session.userName,
                email: session.email || '',
                subject: session.subject || '',
                joinedAt: session.joinedAt,
                position: queuedSessions.length + 1
              });
            } else {
              // Add to active rooms (for active and recently closed chats)
              if (!activeRooms[session.roomId]) {
                activeRooms[session.roomId] = {
                  userName: session.userName,
                  email: session.email || '',
                  subject: session.subject || '',
                  messages: session.messages || [],
                  joinedAt: session.joinedAt || new Date().toISOString(),
                  isOnline: session.status === 'active',
                  unreadCount: 0,
                  status: session.status,
                  resolved: session.resolved || false,
                  resolvedAt: session.resolvedAt || null,
                  agentId: session.agentId || null,
                  agentName: session.agentName || '',
                  notes: session.notes || ''
                };
              }
            }
          });

          // Update rooms state with active session data including messages
          setRooms(prev => {
            const map = { ...prev, ...activeRooms };
            return map;
          });

          // Update queue state with queued sessions
          setQueue(queuedSessions);
        }
      })
      .catch(err => console.error('Failed to fetch chat sessions:', err));
    };
    
    // Check if socket is already connected, otherwise wait for connection
    if (socket.connected) {
      joinAdminRoom();
    } else {
      socket.on('connect', () => {
        console.log('Socket connected, joining admin room...');
        joinAdminRoom();
      });
    }
    
    socket.on('admin:active_rooms', (active) => {
      setRooms(prev => {
        const map={...prev};
        active.forEach(({roomId,userName,messages, unreadCount, email, subject, agentName, agentId})=>{
          if(!map[roomId]){
            // New room (e.g. after client reconnect with new socket ID) — populate with full data
            map[roomId]={userName,messages:messages||[],joinedAt:new Date().toISOString(),isOnline:true, unreadCount: unreadCount || 0, email: email || '', subject: subject || '', agentName: agentName || '', agentId: agentId || null};
          } else {
            // Existing room — update unreadCount, and backfill messages if we had none
            map[roomId] = {
              ...map[roomId],
              unreadCount: unreadCount ?? map[roomId].unreadCount,
              messages: (map[roomId].messages && map[roomId].messages.length > 0) ? map[roomId].messages : (messages || map[roomId].messages || []),
            };
          }
        });
        return map;
      });
    });
    socket.on('admin:new_user',   ({roomId,userName,email})=>{ setRooms(prev=>({...prev,[roomId]:{userName,messages:[],joinedAt:new Date().toISOString(),isOnline:true, unreadCount:0, email: email || ''}})); notifications.show({title:'New user',message:`${userName} joined`,color:'green'}); });
    socket.on('admin:message',    ({roomId,msg})=>{ 
      // Validate message before adding - prevent invalid objects from being rendered
      if (!msg || typeof msg !== 'object' || typeof msg.text !== 'string') {
        console.warn('Invalid message received:', msg);
        return;
      }
      setRooms(prev=>{ if(!prev[roomId]) return prev; return {...prev,[roomId]:{...prev[roomId],messages:[...prev[roomId].messages,msg]}}; }); 
      if(msg.sender==='user') notifications.show({title:'New message',message:msg.text.substring(0,50),color:'blue'}); 
    });
    socket.on('admin:user_left',  ({roomId})=>{ setRooms(prev=>{const u={...prev};delete u[roomId];return u;}); setSelectedRoom(c=>c===roomId?null:c); });
    socket.on('admin:room_updated', ({oldRoomId, newRoomId})=>{
      console.log('Room updated:', oldRoomId, '->', newRoomId);
      setRooms(prev=>{
        if(!prev[oldRoomId]) return prev;
        const updated={...prev};
        updated[newRoomId] = {...updated[oldRoomId]};
        delete updated[oldRoomId];
        return updated;
      });
      setSelectedRoom(c=>c===oldRoomId?newRoomId:c);
    });
    socket.on('admin:queue_update',    (q)=>setQueue(q));
    socket.on('admin:typing_preview',  ({roomId,text})=>setSneakPeeks(prev=>({...prev,[roomId]:text})));
    socket.on('admin:resolved_update', ({roomId,resolved})=>{ setRooms(prev=>{ if(!prev[roomId]) return prev; return {...prev,[roomId]:{...prev[roomId],resolved}}; }); });
    socket.on('admin:notes_updated', ({roomId, notes})=>{ 
      setRooms(prev=>{ if(!prev[roomId]) return prev; return {...prev,[roomId]:{...prev[roomId],notes}}; });
      if(selectedRoom === roomId) setEditingNotes(notes);
    });
    socket.on('admin:escalation_update', ({roomId, escalated, escalatedTo, escalatedAt})=>{ 
      setEscalatedChats(prev=>({...prev,[roomId]:{escalated, escalatedTo, escalatedAt}}));
      setRooms(prev=>{ if(!prev[roomId]) return prev; return {...prev,[roomId]:{...prev[roomId],escalated, escalatedTo, escalatedAt}}; });
    });
    socket.on('admin:historical_sessions', (sessions) => {
      setHistoricalSessions(prev => {
        // Merge: add new sessions that don't already exist, prepend to top
        const existingIds = new Set(prev.map(s => s._id));
        const newSessions = sessions.filter(s => !existingIds.has(s._id));
        return newSessions.length > 0 ? [...newSessions, ...prev] : (prev.length === 0 ? sessions : prev);
      });
    });
    
    // History cleared response
    socket.on('admin:history_cleared', ({ success, deletedCount, error }) => {
      setClearHistoryModalOpen(false);
      if (success) {
        notifications.show({ title: 'History Cleared', message: `Successfully removed ${deletedCount} chat sessions`, color: 'green' });
      } else {
        notifications.show({ title: 'Error', message: error || 'Failed to clear history', color: 'red' });
      }
    });
    
    // Call event listeners
    socket.on('call:incoming', ({ callType, from, callerSocketId }) => {
      console.log('[WebRTC Admin] Incoming call received:', { callType, from, callerSocketId });
      setIncomingCall({ callType, from });
      setCallTargetRoomId(callerSocketId); // Store the caller's socket ID so we can signal back
      setCallModal(callType);
      notifications.show({ title: 'Incoming call', message: `${from} is calling you (${callType})`, color: 'green' });
    });
    socket.on('call:accepted', ({ acceptedBy }) => {
      console.log('[WebRTC Admin] Call accepted by:', acceptedBy);
      setCallTargetRoomId(acceptedBy); // Store the customer's socket ID for WebRTC signaling
      setCallStartTime(Date.now()); // Start call timer when call is connected
      setCallStatus('connected');
      // Admin initiated the call → admin is the caller → admin creates the offer
      // Pass the acceptedBy directly to avoid state timing issues
      createPeerConnectionAndOffer(acceptedBy);
    });
    socket.on('call:rejected', () => {
      setCallStatus(null);
      setCallModal(null);
      cleanupCall();
      toast('Call was rejected', 'red');
    });
    socket.on('call:ended', ({ duration }) => {
      const durationSec = duration || callDuration;
      const durationStr = formatCallDuration(durationSec);
      
      // Get the room where the call happened - use callTargetRoomId if available, otherwise selectedRoom
      const targetRoom = callTargetRoomId || selectedRoom;
      console.log('[WebRTC Admin] Call ended - targetRoom:', targetRoom, 'callTargetRoomId:', callTargetRoomId, 'selectedRoom:', selectedRoom);
      
      // Always add call ended message to the appropriate room
      if (targetRoom && durationSec > 0) {
        const isMock = ['mock-','q-'].some(p=>targetRoom.startsWith(p)) || /^[gp]\d+$/.test(targetRoom);
        const callEndMsg = {
          sender: 'system',
          text: `📞 Call ended - Duration: ${durationStr}`,
          timestamp: new Date().toISOString(),
          isCallEnded: true,
          callDuration: durationSec
        };
        
        console.log('[WebRTC Admin] Adding call ended message to room:', targetRoom, 'isMock:', isMock);
        
        // Always add locally first - this ensures the message appears even if server fails
        setRooms(prev => {
          const room = prev[targetRoom];
          console.log('[WebRTC Admin] Current room state:', room);
          if (!room) {
            // Room might not exist in state yet - create it with the message
            return {
              ...prev,
              [targetRoom]: { messages: [callEndMsg], userName: 'Unknown' }
            };
          }
          return {
            ...prev,
            [targetRoom]: {...room, messages: [...(room.messages || []), callEndMsg]}
          };
        });
        
        // Note: We don't emit to server here because:
        // 1. We're already adding the message locally below
        // 2. The server would broadcast it back, causing duplicate messages
        // The message will be persisted when it's received from the user side or saved to DB separately
      } else {
        console.log('[WebRTC Admin] No target room or duration - skipping message add');
      }
      
      console.log('[WebRTC Admin] Received call:ended, cleaning up...');
      
      // Clean up all call-related state
      cleanupCall();
      setCallStatus(null);
      setCallModal(null);
      setIncomingCall(null);
      setCallTargetRoomId(null);  // Reset target room ID - important!
      setCallDuration(0);
      setCallStartTime(null);
      toast(`Call ended - Duration: ${durationStr}`, 'blue');
    });
    socket.on('call:offer', async ({ offer, from }) => {
      console.log('[WebRTC Admin] Received offer from:', from);
      await handleOffer(offer, from);
    });
    socket.on('call:answer', async ({ answer }) => {
      console.log('[WebRTC Admin] Received answer');
      await handleAnswer(answer);
    });
    socket.on('call:ice-candidate', async ({ candidate }) => {
      console.log('[WebRTC Admin] Received ICE candidate');
      await handleIceCandidate(candidate);
    });
    
    return ()=>['connect','admin:active_rooms','admin:new_user','admin:message','admin:user_left','admin:room_updated','admin:queue_update','admin:typing_preview','admin:resolved_update','admin:notes_updated','admin:escalation_update','admin:historical_sessions','admin:history_cleared','call:incoming','call:accepted','call:rejected','call:ended','call:offer','call:answer','call:ice-candidate'].forEach(e=>socket.off(e));
  }, []);

  useEffect(() => { messagesEnd.current?.scrollIntoView({behavior:'smooth'}); }, [rooms, selectedRoom]);

  // ── WebRTC Call Functions ──
  // ICE candidate buffer — candidates that arrive before remoteDescription is set
  const iceCandidateBuffer = useRef([]);

  // Helper: get the socket ID of the remote peer we're in a call with
  const getTargetSocketId = () => callTargetRoomId || selectedRoom;

  // Helper: create a new RTCPeerConnection with common config
  const createPeerConnection = (targetSocketId) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ]
    });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[WebRTC Admin] ICE candidate generated');
        socket.emit('call:ice-candidate', { targetSocketId, candidate: event.candidate });
      }
    };

    pc.ontrack = (event) => {
      console.log('[WebRTC Admin] Remote track received:', event.track.kind, 'enabled:', event.track.enabled);
      console.log('[WebRTC Admin] Remote stream ID:', event.streams[0]?.id);
      console.log('[WebRTC Admin] Remote stream tracks:', event.streams[0]?.getTracks().map(t => t.kind));
      setRemoteStream(event.streams[0]);
    };

    pc.onconnectionstatechange = () => {
      console.log('[WebRTC Admin] Connection state:', pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        toast('Call connection lost', 'red');
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC Admin] ICE connection state:', pc.iceConnectionState);
    };

    peerConnection.current = pc;
    return pc;
  };

  // Helper: get local media stream
  const getLocalMedia = async (isVideo) => {
    console.log('[WebRTC Admin] Requesting media, video:', isVideo);
    const stream = await navigator.mediaDevices.getUserMedia({
      video: isVideo,
      audio: true,
    });
    console.log('[WebRTC Admin] Got local stream, tracks:', stream.getTracks().map(t => t.kind));
    setLocalStream(stream);
    return stream;
  };

  // Called when admin is the CALLER (admin initiated call, client accepted)
  const createPeerConnectionAndOffer = async (explicitTarget) => {
    try {
      // Use explicit target if provided, otherwise fall back to state
      const target = explicitTarget || getTargetSocketId();
      console.log('[WebRTC Admin] Creating offer for target:', target, explicitTarget ? '(explicit)' : '(from state)');
      const isVideo = callModal === 'video';
      const stream = await getLocalMedia(isVideo);
      const pc = createPeerConnection(target);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      console.log('[WebRTC Admin] Sending offer to:', target);
      socket.emit('call:offer', { targetSocketId: target, offer });
    } catch (err) {
      console.error('[WebRTC Admin] Error creating offer:', err);
      handleMediaError(err);
    }
  };

  // Called when admin receives an offer (admin is the CALLEE)
  const handleOffer = async (offer, fromSocketId) => {
    try {
      const target = fromSocketId || getTargetSocketId();
      // Store who we're talking to if not already set
      if (!callTargetRoomId) setCallTargetRoomId(target);
      
      console.log('[WebRTC Admin] Handling offer from:', target);
      const isVideo = callModal === 'video';
      const stream = await getLocalMedia(isVideo);
      const pc = createPeerConnection(target);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      await pc.setRemoteDescription(new RTCSessionDescription(offer));

      // Flush any buffered ICE candidates
      while (iceCandidateBuffer.current.length > 0) {
        const buffered = iceCandidateBuffer.current.shift();
        await pc.addIceCandidate(new RTCIceCandidate(buffered));
        console.log('[WebRTC Admin] Flushed buffered ICE candidate');
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log('[WebRTC Admin] Sending answer to:', target);
      socket.emit('call:answer', { targetSocketId: target, answer });
    } catch (err) {
      console.error('[WebRTC Admin] Error handling offer:', err);
      handleMediaError(err);
    }
  };

  const handleAnswer = async (answer) => {
    if (peerConnection.current) {
      console.log('[WebRTC Admin] Setting remote description from answer');
      await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
      // Flush any buffered ICE candidates
      while (iceCandidateBuffer.current.length > 0) {
        const buffered = iceCandidateBuffer.current.shift();
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(buffered));
        console.log('[WebRTC Admin] Flushed buffered ICE candidate');
      }
    }
  };

  const handleIceCandidate = async (candidate) => {
    if (peerConnection.current && peerConnection.current.remoteDescription) {
      try {
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        console.error('[WebRTC Admin] Error adding ICE candidate:', error);
      }
    } else {
      // Buffer the candidate until remoteDescription is set
      console.log('[WebRTC Admin] Buffering ICE candidate (no remote description yet)');
      iceCandidateBuffer.current.push(candidate);
    }
  };

  const handleMediaError = (err) => {
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      toast('Camera/Microphone access denied. Please allow permissions in browser settings.', 'red');
    } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      toast('No camera or microphone found.', 'red');
    } else {
      toast('Could not access camera/microphone', 'red');
    }
    cleanupCall();
    setCallStatus(null);
    setCallModal(null);
  };

  const initiateCall = (callType) => {
    const targetRoom = selectedRoom;
    console.log('[WebRTC Admin] Initiating call:', { callType, targetRoom });
    if (!targetRoom) {
      toast('Please select a customer first', 'orange');
      return;
    }
    setCallTargetRoomId(targetRoom);
    setCallModal(callType);
    setCallStatus('calling');
    socket.emit('call:initiate', { roomId: targetRoom, callType });
    toast(`Calling ${selectedData?.userName || 'Client'}...`, 'blue');
  };

  const acceptCall = () => {
    // Admin is answering an incoming call — the caller will send the offer
    // We just notify the caller that we accepted
    const target = callTargetRoomId;
    console.log('[WebRTC Admin] Accepting call from:', target);
    socket.emit('call:accept', { targetSocketId: target });
    setCallStatus('connected');
    setCallStartTime(Date.now()); // Start call timer when call is connected
    setIncomingCall(null);
    // Don't create offer here — wait for the caller's offer via call:offer event
  };

  const rejectCall = () => {
    const target = callTargetRoomId;
    socket.emit('call:reject', { targetSocketId: target });
    setCallModal(null);
    setIncomingCall(null);
    setCallStatus(null);
    setCallTargetRoomId(null);
    iceCandidateBuffer.current = [];
  };

  // Cleanup media & peer connection without emitting socket events
  const cleanupCall = () => {
    console.log('[WebRTC Admin] Cleaning up call - localStream:', !!localStream, 'remoteStream:', !!remoteStream, 'peerConnection:', !!peerConnection.current);
    
    // Stop all local stream tracks
    if (localStream) {
      localStream.getTracks().forEach(track => {
        console.log('[WebRTC Admin] Stopping local track:', track.kind);
        track.stop();
      });
      setLocalStream(null);
    }
    
    // Close peer connection
    if (peerConnection.current) {
      // Get all tracks from the connection before closing
      peerConnection.current.getSenders().forEach(sender => {
        if (sender.track) {
          console.log('[WebRTC Admin] Stopping sender track:', sender.track.kind);
          sender.track.stop();
        }
      });
      peerConnection.current.close();
      peerConnection.current = null;
    }
    
    // Stop remote stream tracks
    if (remoteStream) {
      remoteStream.getTracks().forEach(track => {
        console.log('[WebRTC Admin] Stopping remote track:', track.kind);
        track.stop();
      });
      setRemoteStream(null);
    }
    
    iceCandidateBuffer.current = [];
    console.log('[WebRTC Admin] Call cleanup complete');
  };

  const endCall = () => {
    const target = getTargetSocketId();
    // Calculate duration from callStartTime to ensure accurate duration
    const duration = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : callDuration;
    const durationStr = formatCallDuration(duration);
    const targetRoom = callTargetRoomId || selectedRoom;
    
    console.log('[WebRTC Admin] Ending call - duration:', duration, 'callStartTime:', callStartTime);
    
    // Emit call transaction
    if (targetRoom && duration > 0) {
      const userData = JSON.parse(localStorage.getItem('helpdeskUser') || '{}');
      socket.emit('admin:call_ended', {
        roomId: targetRoom,
        clientName: selectedData?.userName || 'Unknown',
        clientEmail: selectedData?.email || 'N/A',
        agentName: userData.fullName || userData.username || 'Support Agent',
        agentId: userData._id,
        duration: duration,
        callType: callModal,
        timestamp: new Date().toISOString()
      });
    }
    
    // Send call end with duration
    socket.emit('call:end', { targetSocketId: target, duration: duration });
    
    // Add system message to chat about call ended
    if (targetRoom && duration > 0) {
      const isMock = ['mock-','q-'].some(p=>targetRoom.startsWith(p)) || /^[gp]\d+$/.test(targetRoom);
      const callEndMsg = {
        sender: 'system',
        text: `📞 Call ended - Duration: ${durationStr}`,
        timestamp: new Date().toISOString(),
        isCallEnded: true,
        callDuration: duration
      };
      // Always add locally - we don't emit to server here because:
      // 1. We're already adding the message locally
      // 2. The server would broadcast it back via admin:message, causing duplicates
      // The message will be persisted when received from user or saved to DB separately
      if (isMock) {
        setRooms(prev => ({
          ...prev,
          [targetRoom]: {...prev[targetRoom], messages: [...(prev[targetRoom]?.messages || []), callEndMsg]}
        }));
      } else {
        setRooms(prev => {
          const room = prev[targetRoom];
          if (!room) return prev;
          return {
            ...prev,
            [targetRoom]: {...room, messages: [...(room.messages || []), callEndMsg]}
          };
        });
      }
    }
    
    cleanupCall();
    setCallStatus(null);
    setCallModal(null);
    setCallTargetRoomId(null);
    setCallDuration(0);
    setCallStartTime(null);
    toast(`Call ended - Duration: ${durationStr}`, 'blue');
  };

  const sendReply = () => {
    if (!reply.trim() || !selectedRoom) return;
    const isMock = ['mock-','q-'].some(p=>selectedRoom.startsWith(p)) || /^[gp]\d+$/.test(selectedRoom);
    if (isMock) {
      const msg={sender:'admin',text:reply,timestamp:new Date().toISOString()};
      setRooms(prev=>({...prev,[selectedRoom]:{...prev[selectedRoom],messages:[...(prev[selectedRoom]?.messages||[]),msg]}}));
    } else {
      socket.emit('admin:message',{roomId:selectedRoom,message:reply});
    }
    setReply(''); setShowEmoji(false);
  };

  const acceptUser=(socketId,userName)=>{
    const cannedMsg = 'Hello! Thank you for contacting our support. How can I assist you today?';
    if(socketId.startsWith('q-')){ 
      const rid=socketId; 
      const msg={sender:'admin',text:cannedMsg,timestamp:new Date().toISOString()};
      setRooms(prev=>({...prev,[rid]:{userName,messages:[msg],joinedAt:new Date().toISOString(),isOnline:true, unreadCount:0, email: ''}})); 
      setQueue(prev=>prev.filter(q=>q.socketId!==socketId).map((q,i)=>({...q,position:i+1}))); 
      setSelectedRoom(rid); 
      toast(`✅ Accepted ${userName}`,'green'); 
    }
    else{ socket.emit('admin:accept',{socketId}); setQueue(prev=>prev.filter(q=>q.socketId!==socketId).map((q,i)=>({...q,position:i+1}))); }
  };
  const rejectUser=(socketId,userName)=>{
    if(socketId.startsWith('q-')){ setQueue(prev=>prev.filter(q=>q.socketId!==socketId).map((q,i)=>({...q,position:i+1}))); toast(`Dismissed ${userName}`,'orange'); }
    else{ socket.emit('admin:reject',{socketId}); setQueue(prev=>prev.filter(q=>q.socketId!==socketId).map((q,i)=>({...q,position:i+1}))); }
  };
  const closeChat=(roomId)=>{ if(!(['mock-','q-'].some(p=>roomId.startsWith(p)) || /^[gp]\d+$/.test(roomId))) socket.emit('admin:close',{roomId}); setRooms(prev=>{const u={...prev};delete u[roomId];return u;}); setSelectedRoom(c=>c===roomId?null:c); toast('Chat closed','red'); };
  const addContact=(name)=>{ if(addedContacts.includes(name)){toast(`${name} already added`,'gray');return;} setAddedContacts(p=>[...p,name]); toast(`✅ ${name} added`,'green'); };
  const insertEmoji=(e)=>{ setReply(p=>p+e); setShowEmoji(false); };

  const selectedData = selectedRoom ? rooms[selectedRoom] : null;
  const filteredRooms = Object.entries(rooms).filter(([,d])=>{
    const matchesSearch = d.userName.toLowerCase().includes(search.toLowerCase());
    if(statusFilter === 'all') return matchesSearch && !d.resolved && !d.escalated; // All tab shows only unresolved (excluding escalated)
    if(statusFilter === 'open') return matchesSearch && !d.resolved && !d.escalated; // Open tab excludes escalated
    if(statusFilter === 'resolved') return matchesSearch && d.resolved;
    return matchesSearch;
  });

  /* ── Render room list ── */
  const renderRooms = () => {
    const mkItem=(id,name,preview,time,isOnline=false,resolved=false,unreadCount=0,joinedAt=null)=>{
      const chatAge = getChatAgeMinutes(joinedAt);
      const isSlaBreached = !resolved && chatAge > SLA_THRESHOLD_MINUTES;
      const isSlaWarning = !resolved && chatAge > SLA_THRESHOLD_MINUTES * 0.7;
      
      return (
      <UnstyledButton key={id} className={`${classes.roomItem} ${selectedRoom===id?classes.roomItemActive:''}`}
        onClick={()=>{ 
          setSelectedRoom(id); 
          // Reset unread count when opening conversation - notify backend
          if(rooms[id]?.unreadCount > 0){
            socket.emit('admin:mark_read', { roomId: id });
            setRooms(prev => ({
              ...prev,
              [id]: {...prev[id], unreadCount: 0}
            }));
          }
          if(!rooms[id]) setRooms(p=>({...p,[id]:{userName:name,messages:[],joinedAt:new Date().toISOString(),isOnline:true, unreadCount:0, email: ''}})); 
        }}>
        <Indicator position="bottom-end" size={9} color={isOnline?'green':'gray'} offset={3} withBorder>
          <Avatar color={avatarColor(name)} radius="xl" size={38}>{getInitial(name)}</Avatar>
        </Indicator>
        <Box style={{flex:1,overflow:'hidden'}}>
          <Group justify="space-between" gap={0} wrap="nowrap">
            <Group gap={4} style={{maxWidth:resolved?'calc(100% - 50px)':'100%'}}>
              <Text size="sm" fw={600} c="#2d2d2d" truncate style={{maxWidth:resolved?85:120}}>{name}</Text>
              {resolved && <Badge size="xs" color="green" variant="filled" radius="xl">✓</Badge>}
              {isSlaBreached && <Badge size="xs" color="red" variant="filled" radius="xl">⚠️</Badge>}
              {isSlaWarning && !isSlaBreached && <Badge size="xs" color="orange" variant="filled" radius="xl">⏱️</Badge>}
              {unreadCount > 0 && <Badge size="xs" color="red" variant="filled" radius="xl">{unreadCount}</Badge>}
            </Group>
            <Text size="xs" c="dimmed" style={{flexShrink:0}}>{time}</Text>
          </Group>
          <Group gap={4}>
            <Text size="xs" c="dimmed" truncate style={{flex:1}}>{preview}</Text>
            {!resolved && joinedAt && (
              <Text size="xs" c={isSlaBreached ? 'red' : isSlaWarning ? 'orange' : 'dimmed'} fw={500}>
                {formatResolutionTime(chatAge)}
              </Text>
            )}
          </Group>
        </Box>
      </UnstyledButton>
    );};
    
    if(activeTab==='Voucher') {
      const displayEmail = selectedData?.email || voucherSearchEmail;
      
      return (
        <Box p="md" style={{flex:1,overflow:'auto'}}>
          <Text size="sm" fw={600} c="#2d2d2d" mb="md">🎫 Voucher Diagnostics</Text>
          
          {!selectedData?.email && (
            <TextInput
              label="Search by Email"
              placeholder="Enter client email"
              value={voucherSearchEmail}
              onChange={(e) => setVoucherSearchEmail(e.target.value)}
              mb="md"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && voucherSearchEmail.trim()) {
                  toast(`Searching voucher for ${voucherSearchEmail}...`, 'blue');
                }
              }}
              rightSection={
                <ActionIcon variant="light" color="violet" onClick={() => {
                  if (voucherSearchEmail.trim()) {
                    toast(`Searching voucher for ${voucherSearchEmail}...`, 'blue');
                  } else {
                    toast('Please enter an email address', 'orange');
                  }
                }}>
                  <IconSearch size={14} />
                </ActionIcon>
              }
            />
          )}
          
          {displayEmail && (
            <VoucherDiagnosticSidebar
              email={displayEmail}
              onClose={() => {}}
            />
          )}
          
          {!displayEmail && (
            <Text size="sm" c="dimmed" ta="center">
              Select a conversation from the Direct tab or search by email above to view voucher diagnostics.
            </Text>
          )}
        </Box>
      );
    }

    // Calls / Transactions tab
    if(activeTab==='Calls') {
      return (
        <Box p="md" style={{flex:1,overflow:'auto'}}>
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" fw={600} c="#2d2d2d"> Call Transactions</Text>
              <Group>
                <TextInput
                  placeholder="Search..."
                  leftSection={<IconSearch size={14} />}
                  value={transactionSearch}
                  onChange={(e) => setTransactionSearch(e.target.value)}
                  size="xs"
                  w={250}
                  styles={{ input: { background: '#ffffff', border: '1px solid #e8e5f0', color: '#2d2d2d' } }}
                />
              </Group>
            </Group>

            {transactionsLoading ? (
              <Box ta="center" py="xl">
                <Text c="dimmed">Loading transactions...</Text>
              </Box>
            ) : transactions.length === 0 ? (
              <Box ta="center" py="xl">
                <Text c="dimmed">No call transactions found</Text>
              </Box>
            ) : (
              <>
                <Paper p="md" style={{background:'#ffffff',border:'1px solid #e8e5f0',maxHeight:'500px',overflow:'auto'}}>
                  <Table highlightOnHover striped size="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th c="dimmed" style={{fontSize:11}}>Customer</Table.Th>
                        <Table.Th c="dimmed" style={{fontSize:11}}>Contact</Table.Th>
                        <Table.Th c="dimmed" style={{fontSize:11}}>Duration</Table.Th>
                        <Table.Th c="dimmed" style={{fontSize:11}}>Agent Handled</Table.Th>
                        <Table.Th c="dimmed" style={{fontSize:11}}>Status</Table.Th>
                        <Table.Th c="dimmed" style={{fontSize:11}}>Date/Time</Table.Th>
                        <Table.Th c="dimmed" style={{fontSize:11}}>Rating</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {transactions.map((t) => (
                        <Table.Tr key={t._id}>
                          <Table.Td>
                            <Group gap="xs">
                              <Avatar 
                                size={24}
                                color={avatarColor(t.userName)}
                                radius="xl"
                              >
                                {getInitial(t.userName)}
                              </Avatar>
                              <Box style={{flex:1}}>
                                <Text size="xs" fw={500} c="#2d2d2d" truncate>{t.userName || 'Unknown'}</Text>
                              </Box>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Box>
                              {t.customerEmail && <Text size="xs" c="dimmed" truncate>{t.customerEmail}</Text>}
                              {t.customerPhone && <Text size="xs" c="dimmed" truncate>{t.customerPhone}</Text>}
                              {!t.customerEmail && !t.customerPhone && <Text size="xs" c="dimmed">-</Text>}
                            </Box>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">
                              {t.callDuration > 0 
                                ? `${Math.floor(t.callDuration / 60)}m ${t.callDuration % 60}s`
                                : '-'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Box>
                              <Text size="xs" fw={500} c="#2d2d2d" truncate>{t.agentName || 'Unassigned'}</Text>
                              {t.agentEmail && <Text size="xs" c="dimmed" truncate>{t.agentEmail}</Text>}
                            </Box>
                          </Table.Td>
                          <Table.Td>
                            <Stack gap="xs">
                              <Badge 
                                size="xs"
                                color={t.status === 'Resolved' ? 'green' : 'orange'}
                                variant="dot"
                              >
                                {t.status === 'Resolved' ? '✅' : '⬆️'} {t.status}
                              </Badge>
                              {t.rating && (
                                <Tooltip label={t.feedback || 'No feedback'} position="top">
                                  <Badge size="xs" color="yellow" variant="light" style={{cursor: 'pointer'}}>
                                    ⭐ {t.rating}/5
                                  </Badge>
                                </Tooltip>
                              )}
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed" truncate>
                              {t.callStartTime ? new Date(t.callStartTime).toLocaleString() : '-'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {t.rating ? (
                              <Tooltip label={t.feedback || 'No feedback'} position="top">
                                <Badge size="xs" color="yellow" variant="light" style={{cursor: 'pointer'}}>
                                  ⭐ {t.rating}/5
                                </Badge>
                              </Tooltip>
                            ) : (
                              <Text size="xs" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>

                {/* Pagination */}
                <Group justify="center">
                  <Button
                    disabled={transactionsPage <= 1}
                    size="xs"
                    variant="subtle"
                    onClick={() => setTransactionsPage(p => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Text size="xs" c="dimmed">
                    Page {transactionsPage} of {Math.ceil(transactionsTotal / 10)}
                  </Text>
                  <Button
                    disabled={transactionsPage >= Math.ceil(transactionsTotal / 10)}
                    size="xs"
                    variant="subtle"
                    onClick={() => setTransactionsPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Box>
      );
    }
    
    if(activeTab==='Group')  return GROUP_ROOMS.filter(r=>r.name.toLowerCase().includes(search.toLowerCase())).map(r=>mkItem(r.id,r.name,r.preview,r.time));
    if(activeTab==='Public') return PUBLIC_ROOMS.filter(r=>r.name.toLowerCase().includes(search.toLowerCase())).map(r=>mkItem(r.id,r.name,r.preview,r.time));
    
    // Sort rooms: unread messages first, then by most recent
    const sortedRooms = [...filteredRooms].sort((a, b) => {
      const aUnread = a[1]?.unreadCount || 0;
      const bUnread = b[1]?.unreadCount || 0;
      if (aUnread !== bUnread) return bUnread - aUnread; // Higher unread count first
      const aTime = a[1]?.messages?.[a[1].messages.length - 1]?.timestamp || '';
      const bTime = b[1]?.messages?.[b[1].messages.length - 1]?.timestamp || '';
      return new Date(bTime) - new Date(aTime); // Most recent first
    });
    
    return sortedRooms.length===0
      ? <Text size="xs" c="dimmed" ta="center" mt="lg">No active users</Text>
      : sortedRooms.map(([id,d])=>{ const lm=d.messages[d.messages.length-1]; const msgText = (lm && typeof lm === 'object' && lm.text && typeof lm.text === 'string') ? lm.text.substring(0,28) + (lm.text.length > 28 ? '…' : '') : 'No messages'; return mkItem(id,d.userName,msgText,lm && typeof lm === 'object' ? formatTime(lm.timestamp || Date.now()) : '',d.isOnline,d.resolved,d.unreadCount||0,d.joinedAt); });
  };

  /* ──────────────────────────
     RENDER
  ────────────────────────── */
  return (
    <Box className={classes.root}>

      {/* ══ HEADER ══ */}
      <Box className={classes.header}>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="xs">
            <Text fw={700} c="#2d2d2d" size="md">💬 Chat Bot</Text>
            <Group gap={2} visibleFrom="sm">
              {[['Chat',IconMessage],['Call',IconPhone],['Mail',IconMail],['Contacts',IconUsers]].map(([lbl,Ic])=>(
                <Tooltip key={lbl} label={lbl} position="bottom" withArrow>
                  <ActionIcon variant="subtle" color="gray" onClick={()=>toast(`${lbl} clicked`)}>
                    <Ic size={16}/>
                  </ActionIcon>
                </Tooltip>
              ))}
            </Group>
          </Group>
          <Group gap={4}>
            <Tooltip label="Voucher Search">
              <Button 
                variant="light" 
                color="violet" 
                size="xs"
                leftSection={<IconSearch size={14} />}
                onClick={() => setVoucherSidebarOpen(true)}
              >
                Voucher
              </Button>
            </Tooltip>
            <Tooltip label="View Latest Purchased Voucher">
              <Button 
                variant="light" 
                color="violet" 
                size="xs"
                leftSection={<IconHistory size={14} />}
                onClick={() => setLatestVoucherOpened(true)}
              >
                Latest
              </Button>
            </Tooltip>
            <Tooltip label="Notifications"><ActionIcon variant="subtle" color="gray" onClick={()=>toast('Notifications')}><IconBell size={17}/></ActionIcon></Tooltip>
            <Tooltip label="Profile"><ActionIcon variant="subtle" color="gray" onClick={()=>setProfileOpen(true)}><IconUser size={17}/></ActionIcon></Tooltip>
            <Tooltip label={asideOpen?'Hide panel':'Show panel'}>
              <ActionIcon variant="subtle" color="gray" onClick={()=>setAsideOpen(o=>!o)}><IconUsers size={17}/></ActionIcon>
            </Tooltip>
            <Tooltip label="Settings"><ActionIcon variant="subtle" color="gray" onClick={()=>toast('Settings')}><IconSettings size={17}/></ActionIcon></Tooltip>
            <Tooltip label="Logout"><ActionIcon variant="subtle" color="red" onClick={handleLogout}><IconLogout size={17}/></ActionIcon></Tooltip>
          </Group>
        </Group>
      </Box>

      {/* ══ BODY ══ */}
      <Box className={classes.body}>

        {/* ── SIDEBAR ── */}
        <Box className={classes.sidebar}>
          {/* Header */}
          <Group px="md" py="sm" justify="space-between" style={{flexShrink:0}}>
            <Text fw={700} c="#2d2d2d">Chats</Text>
            <Tooltip label="New conversation">
              <ActionIcon size="sm" radius="xl" variant="filled" color="blue" onClick={()=>toast('New conversation')}>
                <IconPlus size={14}/>
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Queue panel */}
          <Box mx="sm" mb="xs" className={classes.queueBox}>
            <UnstyledButton w="100%" px="xs" py={7} onClick={()=>setShowQueue(o=>!o)} className={classes.queueToggle}>
              <Group gap="xs" wrap="nowrap">
                <Text size="xs" fw={700} c="#2d2d2d" style={{flex:1}}>🔢 Queue</Text>
                <Badge size="xs" color={queue.length>0?'red':'gray'} variant="filled" radius="xl" style={{minWidth:22}}>{queue.length}</Badge>
                <ActionIcon size="xs" variant="transparent" c="dimmed">{showQueue?<IconChevronUp size={11}/>:<IconChevronDown size={11}/>}</ActionIcon>
              </Group>
            </UnstyledButton>
            {showQueue && (
              <Box pb="xs" px="xs">
                {queue.length===0
                  ? <Text size="xs" c="dimmed" ta="center" py="xs">No users waiting</Text>
                  : queue.map(q=>(
                    <Group key={q.socketId} gap="xs" py={5} className={classes.queueItem} wrap="nowrap">
                      <Badge size="xs" color="blue" variant="light" w={22} p={0} ta="center">#{q.position}</Badge>
                      <Avatar size={26} color={avatarColor(q.userName)} radius="xl">{getInitial(q.userName)}</Avatar>
                      <Box style={{flex:1,overflow:'hidden'}}>
                        <Text size="xs" fw={600} c="#2d2d2d" truncate>{q.userName}</Text>
                        {q.subject && <Text size="xs" c="blue" truncate>{q.subject}</Text>}
                        <Text size="xs" c="dimmed">{timeAgo(q.joinedAt)}</Text>
                      </Box>
                      <Tooltip label="Accept"><ActionIcon size="sm" color="green" variant="light" radius="xl" onClick={()=>acceptUser(q.socketId,q.userName)}><IconCheck size={12}/></ActionIcon></Tooltip>
                      <Tooltip label="Dismiss"><ActionIcon size="sm" color="red"   variant="light" radius="xl" onClick={()=>rejectUser(q.socketId,q.userName)}><IconX size={12}/></ActionIcon></Tooltip>
                    </Group>
                  ))
                }
              </Box>
            )}
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={setActiveTab} variant="pills" px="sm" mb="xs"
            styles={{tab:{fontSize:12,padding:'5px 12px'},list:{gap:4}}}>
            <Tabs.List>
              <Tabs.Tab value="Direct">Direct</Tabs.Tab>
              <Tabs.Tab value="Group">Group</Tabs.Tab>
              <Tabs.Tab value="Public">Public</Tabs.Tab>
              <Tabs.Tab value="History">History</Tabs.Tab>
              <Tabs.Tab value="Calls">Calls</Tabs.Tab>
              <Tabs.Tab value="Voucher">🎫 Voucher</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {/* Content based on active tab */}
          {activeTab === 'History' ? (
            <>
              {/* Remove All History Button */}
              {historicalSessions.length > 0 && (
                <Group justify="flex-end" px="xs" pb="xs">
                  <Button 
                    size="xs" 
                    variant="light" 
                    color="red" 
                    leftSection={<IconTrash size={14} />}
                    onClick={handleClearHistory}
                  >
                    Remove All History
                  </Button>
                </Group>
              )}
              <ScrollArea style={{flex:1}} scrollbarSize={4}>
                <Box px="xs">
                {historicalSessions.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" mt="xl">No chat history yet</Text>
                ) : (
                  <Stack gap="xs">
                    {historicalSessions.map((session) => (
                      <Paper key={session.roomId} p="sm" radius="md" 
                        style={{background:'#ffffff',cursor:'pointer'}}
                        onClick={() => {
                          setSelectedRoom(session.roomId);
                          setRooms(prev => {
                            const newRooms = {...prev};
                            newRooms[session.roomId] = {
                              userName: session.userName,
                              messages: session.messages || [],
                              joinedAt: session.joinedAt,
                              isOnline: false,
                              resolved: session.resolved,
                              status: session.status,
                            };
                            return newRooms;
                          });
                        }}>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={600} c="#2d2d2d">{session.userName}</Text>
                          <Badge size="xs" color="gray">{session.status}</Badge>
                        </Group>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {session.messages?.slice(-1)[0]?.text || 'No messages'}
                        </Text>
                        <Text size="xs" c="dimmed" mt="xs">
                          {session.closedAt ? new Date(session.closedAt).toLocaleDateString() : ''}
                        </Text>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
              </ScrollArea>
            </>
          ) : (
            <>
              {/* Search */}
              <TextInput mx="sm" mb="xs" placeholder="Search" size="xs"
                leftSection={<IconSearch size={13}/>} value={search} onChange={e=>setSearch(e.target.value)}
                rightSection={search?<ActionIcon size="xs" variant="subtle" onClick={()=>setSearch('')}><IconX size={12}/></ActionIcon>:null}
                styles={{input:{background:'#ffffff',border:'1px solid #e8e5f0',color:'#2d2d2d'}}}/>

              {/* Status Filter */}
              <SegmentedControl mx="sm" mb="xs" size="xs" value={statusFilter} onChange={setStatusFilter}
                data={[{label:'Inbox',value:'all'},{label:'Open',value:'open'},{label:'Resolved',value:'resolved'}]}
                styles={{root:{background:'#f5f3ff'},indicator:{background:'#9381ff'},label:{color:'#666666'},'__label[data-active]':{color:'#fff'}}}/>

              {/* Room list */}
              <ScrollArea style={{flex:1}} scrollbarSize={4}>
                <Box px="xs">{renderRooms()}</Box>
              </ScrollArea>
            </>
          )}
        </Box>

        {/* ── MAIN CHAT ── */}
        <Box className={classes.main}>
          {!selectedRoom ? (
            <Flex h="100%" align="center" justify="center" direction="column" gap="md" c="dimmed">
              <Text size="4rem">💬</Text>
              <Text fw={700} size="lg" c="gray.5">Select a conversation</Text>
              <Text size="sm" maw={280} ta="center">Pick a user from the sidebar to start replying to their support request.</Text>
            </Flex>
          ) : (
            <Flex direction="column" h="100%">
              {/* Chat Header */}
              <Box className={classes.chatHeader}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap">
                    <Indicator position="bottom-end" size={11} color={selectedData?.isOnline?'green':'gray'} offset={4} withBorder>
                      <Avatar color={avatarColor(selectedData?.userName)} radius="xl" size={42}>{getInitial(selectedData?.userName)}</Avatar>
                    </Indicator>
                    <Box>
                      <Group gap="xs">
                        <Text fw={700} c="#2d2d2d" size="sm">{selectedData?.userName}</Text>
                        {selectedData?.resolved && <Badge size="xs" color="green" variant="filled">Resolved</Badge>}
                        {selectedData?.escalated && <Badge size="xs" color="orange" variant="filled">Escalated</Badge>}
                        {selectedData?.notes && <Badge size="xs" color="blue" variant="light" leftSection={<IconFileText size={10}/>}>Notes</Badge>}
                      </Group>
                      <Text size="xs" c={selectedData?.isOnline?'green':'dimmed'}>
                        {selectedData?.isOnline?'● Online now':`Last seen ${timeAgo(selectedData?.joinedAt)}`}
                      </Text>
                    </Box>
                  </Group>
                  <Group gap="xs" wrap="nowrap" align="center">
                    {/* Resolved and Escalate buttons at the top of conversation */}
                    <Button
                      size="xs"
                      variant={selectedData?.resolved ? 'filled' : 'light'}
                      color={selectedData?.resolved ? 'green' : 'gray'}
                      leftSection={<IconCheck size={14}/>}
                      onClick={()=>{
                        const newResolved = !selectedData?.resolved;
                        const resolvedAt = newResolved ? new Date().toISOString() : null;
                        socket.emit('admin:resolve',{roomId:selectedRoom,resolved:newResolved,resolvedAt});
                        setRooms(prev=>({...prev,[selectedRoom]:{...prev[selectedRoom],resolved:newResolved,resolvedAt}}));
                        if (newResolved) {
                          const timeToResolve = getChatAgeMinutes(selectedData?.joinedAt);
                          toast(`✅ Marked as resolved (Time: ${formatResolutionTime(timeToResolve)}) - Chat will auto-close & customer can rate`, 'green');
                          setTimeout(() => {
                            setRooms(prev => {
                              const updated = {...prev};
                              delete updated[selectedRoom];
                              return updated;
                            });
                            setSelectedRoom(null);
                          }, 2000);
                        } else {
                          toast('Marked as unresolved', 'orange');
                        }
                      }}
                    >
                      {selectedData?.resolved ? 'Resolved' : 'Resolve'}
                    </Button>
                    <Button
                      size="xs"
                      variant={selectedData?.escalated ? 'filled' : 'light'}
                      color={selectedData?.escalated ? 'orange' : 'orange'}
                      leftSection={<IconAlertTriangle size={14}/>}
                      onClick={()=>{
                        if (selectedData?.escalated) {
                          handleRemoveEscalation();
                        } else {
                          setEscalateNotes(selectedData?.notes || '');
                          setEscalateError('');
                          setEscalateModalOpen(true);
                        }
                      }}
                    >
                      {selectedData?.escalated ? 'Escalated' : 'Escalate'}
                    </Button>
                    <Divider orientation="vertical" color="dark.5"/>
                    {/* Agent identity in chat header — clickable */}
                    <Tooltip label="View agent profile" position="bottom">
                      <UnstyledButton onClick={()=>setProfileOpen(true)} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 10px',borderRadius:20,border:'1px solid #e8e5f0',background:'#ffffff',transition:'background .15s'}}
                        className={classes.agentBadge}>
                        <Indicator size={9} color="green" offset={2} position="bottom-end" withBorder>
                          <Avatar size={28} variant="gradient" gradient={{from:'blue',to:'violet'}} radius="xl">{currentAgent ? currentAgent.avatar : AGENT.avatar}</Avatar>
                        </Indicator>
                        <Box visibleFrom="sm">
                          <Text size="xs" fw={600} c="#2d2d2d" lh={1.2}>{currentAgent ? currentAgent.name : AGENT.name}</Text>
                          <Text size="xs" c="dimmed" lh={1.2}>{currentAgent ? currentAgent.role : AGENT.role}</Text>
                        </Box>
                      </UnstyledButton>
                    </Tooltip>
                    <Divider orientation="vertical" color="dark.5"/>
                    <Tooltip label="Voice call"><ActionIcon variant="light" color="blue" radius="xl" onClick={()=>initiateCall('audio')}><IconPhone size={15}/></ActionIcon></Tooltip>
                    <Tooltip label={selectedData?.resolved ? 'Mark as unresolved' : 'Mark as resolved'}>
                      <ActionIcon variant="light" color={selectedData?.resolved ? 'green' : 'gray'} radius="xl" onClick={()=>{ 
                        const newResolved = !selectedData?.resolved; 
                        const resolvedAt = newResolved ? new Date().toISOString() : null;
                        socket.emit('admin:resolve',{roomId:selectedRoom,resolved:newResolved,resolvedAt}); 
                        setRooms(prev=>({...prev,[selectedRoom]:{...prev[selectedRoom],resolved:newResolved,resolvedAt}})); 
                        if (newResolved) {
                          const timeToResolve = getChatAgeMinutes(selectedData?.joinedAt);
                          toast(`✅ Marked as resolved (Time: ${formatResolutionTime(timeToResolve)})`, 'green');
                          // Auto-switch to Resolved tab to show the chat moved
                          setStatusFilter('resolved');
                        } else {
                          toast('Marked as unresolved', 'orange');
                          // Show in Open tab when unresolving
                          setStatusFilter('open');
                        }
                      }}>
                        {selectedData?.resolved ? <IconCheck size={15}/> : <IconX size={15}/>}
                      </ActionIcon>
                    </Tooltip>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="light" color="gray" radius="xl"><IconDots size={15}/></ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown style={{background:'#ffffff',border:'1px solid #e8e5f0'}}>
                        <Menu.Item leftSection={<IconUserPlus size={14}/>} onClick={()=>{ fetchManagers(); setTransferModalOpen(true); }}>Transfer to Manager</Menu.Item>
                        <Menu.Item leftSection={<IconAlertTriangle size={14}/>} color="orange" onClick={()=>{ if (selectedData?.escalated) { handleRemoveEscalation(); } else { setEscalateNotes(selectedData?.notes || ''); setEscalateError(''); setEscalateModalOpen(true); } }}>{selectedData?.escalated ? 'Remove Escalation' : 'Escalate to Monitor'}</Menu.Item>
                        {selectedData?.escalated && <Menu.Item leftSection={<IconCheck size={14}/>} color="green" onClick={()=>{ handleRemoveEscalation(); }}>Remove Escalation</Menu.Item>}
                        <Menu.Divider/>
                        <Menu.Item leftSection={<IconVolume3 size={14}/>} onClick={()=>toast('Muted')}>Mute</Menu.Item>
                        <Menu.Item leftSection={<IconPin size={14}/>} onClick={()=>toast('Pinned')}>Pin</Menu.Item>
                        <Menu.Item leftSection={<IconId size={14}/>} onClick={()=>toast('View profile')}>View profile</Menu.Item>
                        <Menu.Item leftSection={<IconBan size={14}/>} onClick={()=>toast('Blocked','orange')}>Block user</Menu.Item>
                        <Menu.Divider/>
                        <Menu.Item leftSection={<IconFileText size={14}/>} onClick={()=>{ setEditingNotes(selectedData?.notes || ''); setNotesModalOpen(true); }}>Add/Edit Notes</Menu.Item>
                        <Menu.Item leftSection={<IconTrash size={14}/>} color="red" onClick={()=>closeChat(selectedRoom)}>Close chat</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Group>
              </Box>

              {/* Messages */}
              <ScrollArea style={{flex:1}} px="lg" py="md" scrollbarSize={4}>
                <Stack gap="xs" px="md" py="sm">
                  {selectedData?.messages.length===0 && <Text size="xs" c="dimmed" ta="center" mt="xl">No messages yet — waiting for the user…</Text>}
                  {selectedData?.messages.map((msg,i)=>{
                    const isUser=msg.sender==='user';
                    const isSystem=msg.sender==='system' || msg.isCallEnded;
                    const showTime=i===0||Math.abs(new Date(msg.timestamp)-new Date(selectedData.messages[i-1].timestamp))>300000;
                    
                    // Special rendering for system messages (call ended)
                    if (isSystem) {
                      return (
                        <React.Fragment key={i}>
                          {showTime&&<Text size="xs" c="dimmed" ta="center" my={4}>{formatTime(msg.timestamp)}</Text>}
                          <Paper radius="md" px="md" py="sm" maw="80%" mx="auto"
                            style={{background:'#ede9ff',border:'1px solid #e8e5f0',borderRadius:'14px',textAlign:'center'}}>
                            <Text size="sm" c="#9381ff" fw={500}>{msg.text}</Text>
                          </Paper>
                        </React.Fragment>
                      );
                    }
                    
                    return (
                      <React.Fragment key={i}>
                        {showTime&&<Text size="xs" c="dimmed" ta="center" my={4}>{formatTime(msg.timestamp)}</Text>}
                        <Flex justify={isUser?'flex-start':'flex-end'} align="flex-end" gap="xs">
                          {isUser&&<Avatar size={30} color={avatarColor(selectedData.userName)} radius="xl">{getInitial(selectedData.userName)}</Avatar>}
                          <Paper radius="md" px="md" py="sm" maw="58%"
                            style={{background:isUser?'#ede9ff':'linear-gradient(135deg,#9381ff,#b8b8ff)',borderRadius:isUser?'14px 14px 14px 4px':'14px 14px 4px 14px'}}>
                            <Text size="xs" c={isUser?'dimmed':'rgba(255,255,255,0.7)'} fw={600} mb={2}>{isUser?selectedData.userName:'Helpdesk Agent'}</Text>
                            <Text size="sm" c="#2d2d2d">{msg.text}</Text>
                            <Text size="xs" c={isUser?'dimmed':'rgba(255,255,255,0.5)'} ta="right" mt={3}>{formatTime(msg.timestamp)}</Text>
                          </Paper>
                          {!isUser&&(
                            <Tooltip label="View agent profile" position="left">
                              <Avatar size={30} variant="gradient" gradient={{from:'blue',to:'violet'}} radius="xl"
                                style={{cursor:'pointer'}} onClick={()=>setProfileOpen(true)}>{currentAgent ? currentAgent.avatar : AGENT.avatar}</Avatar>
                            </Tooltip>
                          )}
                        </Flex>
                      </React.Fragment>
                    );
                  })}
                </Stack>
                <div ref={messagesEnd}/>
              </ScrollArea>

              {/* Sneak Peek */}
              {sneakPeeks[selectedRoom] && (
                <Box className={classes.sneakPeek}>
                  <Group gap="xs" align="center">
                    <Text size="xs">👁</Text>
                    <Text size="xs" c="dimmed" fw={500}>{selectedData?.userName} is typing:</Text>
                    <Text size="xs" c="blue.4" style={{fontStyle:'italic',flex:1}} truncate>
                      "{sneakPeeks[selectedRoom]}"
                    </Text>
                  </Group>
                </Box>
              )}

              {/* Input Bar */}
              <Box className={classes.inputBar}>
                <TextInput flex={1} placeholder="Say something..." value={reply}
                  onChange={e=>setReply(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendReply();}}}
                  styles={{input:{background:'transparent',border:'none',borderRadius:0,color:'#2d2d2d','&::placeholder':{color:'#6b6894'}}}}/>
                <Group gap={3} style={{flexShrink:0}}>
                  <Box style={{position:'relative'}}>
                    <Tooltip label="Emoji">
                      <ActionIcon variant="subtle" color="gray" onClick={()=>setShowEmoji(o=>!o)}><IconMoodSmile size={17}/></ActionIcon>
                    </Tooltip>
                    {showEmoji&&(
                      <Paper shadow="xl" p="xs" style={{position:'absolute',bottom:40,right:0,zIndex:200,background:'#ffffff',border:'1px solid #e8e5f0',width:196}}>
                        <Group gap={3}>
                          {EMOJIS.map(e=>(
                            <UnstyledButton key={e} onClick={()=>insertEmoji(e)} className={classes.emojiBtn}
                              style={{fontSize:20,padding:4,borderRadius:6}}>{e}</UnstyledButton>
                          ))}
                        </Group>
                      </Paper>
                    )}
                  </Box>
                  <Tooltip label="Canned Responses (⚡)">
                    <ActionIcon variant="light" color="yellow" onClick={()=>setCannedOpen(true)}><IconBolt size={17}/></ActionIcon>
                  </Tooltip>
                  <Divider orientation="vertical" color="dark.5" my={4}/>
                  <Tooltip label="Attach"><ActionIcon variant="subtle" color="gray" onClick={()=>fileInput.current?.click()}><IconPaperclip size={17}/></ActionIcon></Tooltip>
                  <input ref={fileInput} type="file" style={{display:'none'}} onChange={e=>{if(e.target.files[0]){toast(`📎 ${e.target.files[0].name}`,'green');e.target.value='';}}}/>
                  <Tooltip label="Image"><ActionIcon variant="subtle" color="gray" onClick={()=>toast('Image coming soon')}><IconPhoto size={17}/></ActionIcon></Tooltip>
                  <Tooltip label="Audio"><ActionIcon variant="subtle" color="gray" onClick={()=>toast('🎤 Recording...')}><IconMicrophone size={17}/></ActionIcon></Tooltip>
                  <Tooltip label="Camera"><ActionIcon variant="subtle" color="gray" onClick={()=>toast('📷 Camera')}><IconCamera size={17}/></ActionIcon></Tooltip>
                  <ActionIcon size="lg" radius="xl" variant="gradient" gradient={{from:'blue',to:'violet'}} onClick={sendReply}>
                    <IconSend size={15}/>
                  </ActionIcon>
                </Group>
              </Box>
            </Flex>
          )}
        </Box>

        {/* ── RIGHT ASIDE ── */}
        {asideOpen&&(
          <Box className={classes.aside}>
            <ScrollArea h="100%" scrollbarSize={4}>
              <Box p="md">
                <Text fw={700} c="#2d2d2d" mb="md" size="sm">Notification</Text>
                <Stack gap="xs" mb="xl">
                  {notifs.slice(0,4).map(n=>(
                    <UnstyledButton key={n.id} className={classes.notifItem} onClick={()=>toast(`Viewing: ${n.name}`)}>
                      <Group gap="xs" align="flex-start" wrap="nowrap">
                        <Avatar size={32} color={avatarColor(n.name)} radius="xl">{getInitial(n.name)}</Avatar>
                        <Box style={{flex:1,overflow:'hidden'}}>
                          <Group justify="space-between" gap={0} wrap="nowrap">
                            <Text size="xs" fw={600} c="#2d2d2d">{n.name}</Text>
                            <Text size="xs" c="dimmed" style={{flexShrink:0}}>{timeAgo(n.ts)}</Text>
                          </Group>
                          <Text size="xs" c="dimmed" truncate>{n.text}</Text>
                        </Box>
                      </Group>
                    </UnstyledButton>
                  ))}
                </Stack>
              </Box>
            </ScrollArea>
          </Box>
        )}
      </Box>

      {/* ── Canned Responses Drawer ── */}
      <Drawer opened={cannedOpen} onClose={()=>setCannedOpen(false)} position="bottom" size={380}
        title={<Group gap="xs"><IconBolt size={16} color="gold"/><Text fw={700} c="#2d2d2d">Canned Responses</Text></Group>}
        styles={{ content:{ background:'#ffffff', borderTop:'1px solid #e8e5f0' }, header:{ background:'#ffffff', borderBottom:'1px solid #e8e5f0' }, close:{ color:'#666666' } }}>
        <ScrollArea h={300} scrollbarSize={4}>
          <Stack gap="sm" p="sm">
            {CANNED.map(cat=>(
              <Box key={cat.category}>
                <Group gap="xs" mb="xs">
                  <Badge color={cat.color} variant="light" size="sm">{cat.category}</Badge>
                </Group>
                <Stack gap="xs">
                  {cat.items.map(item=>(
                    <UnstyledButton key={item.label}
                      onClick={()=>{ 
                        setReply(item.text); 
                        setCannedOpen(false); 
                        toast(`✅ Template inserted: ${item.label}`,'green');
                        // Auto-resolve and switch to resolved tab when "Resolved" is clicked
                        if(item.label === 'Resolved' && selectedRoom){
                          const resolvedAt = new Date().toISOString();
                          socket.emit('admin:resolve',{roomId:selectedRoom,resolved:true,resolvedAt});
                          setRooms(prev=>({...prev,[selectedRoom]:{...prev[selectedRoom],resolved:true,resolvedAt}}));
                          setStatusFilter('resolved');
                          toast('Chat marked as resolved and switched to Resolved tab','green');
                        }
                      }}
                      style={{ display:'block', width:'100%', cursor:'pointer' }}>
                      <Paper p="xs" radius="md" style={{ background:'#ffffff', border:'1px solid #e8e5f0', transition:'border-color .15s' }}
                        className={classes.cannedItem}>
                        <Group justify="space-between" wrap="nowrap" gap="sm">
                          <Box style={{flex:1, overflow:'hidden'}}>
                            <Text size="xs" fw={700} c="#2d2d2d" mb={2}>{item.label}</Text>
                            <Text size="xs" c="dimmed" truncate>{item.text}</Text>
                          </Box>
                          <Badge size="xs" color={cat.color} variant="filled" style={{flexShrink:0}}>Insert</Badge>
                        </Group>
                      </Paper>
                    </UnstyledButton>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </ScrollArea>
      </Drawer>

      {/* ── Agent Profile Drawer ── */}
      <Drawer opened={profileOpen} onClose={()=>setProfileOpen(false)} position="right" size={380} withCloseButton
        title={<Text fw={700} c="#2d2d2d">My Profile</Text>}
        styles={{
          content: { background:'#ffffff', borderLeft:'1px solid #e8e5f0' },
          header:  { background:'#ffffff', borderBottom:'1px solid #e8e5f0' },
          close:   { color:'#666666' },
        }}>
        <ScrollArea h="calc(100vh - 70px)" scrollbarSize={4}>
          <Stack gap="md" p="md">

            {/* Hero section */}
            <Paper p="xl" radius="lg" style={{ background:'linear-gradient(135deg,#f5f3ff,#ede9ff)', textAlign:'center', position:'relative', overflow:'hidden' }}>
              <Box style={{ position:'absolute', top:-30, right:-30, width:120, height:120, borderRadius:'50%', background:'#f5f3ff' }}/>
              <Box style={{ position:'absolute', bottom:-20, left:-20, width:80, height:80, borderRadius:'50%', background:'#ede9ff' }}/>
              <Indicator size={14} color="green" offset={6} position="bottom-end" withBorder style={{ display:'inline-flex' }}>
                <Avatar size={80} variant="gradient" gradient={{from:'blue',to:'violet'}} radius={80}
                  style={{ margin:'0 auto', fontSize:'2rem', fontWeight:800, cursor:'pointer', boxShadow:'0 0 0 4px rgba(147,129,255,0.15)' }}
                  onClick={()=>toast('Upload profile photo','blue')}>
                  {currentAgent ? currentAgent.avatar : AGENT.avatar}
                </Avatar>
              </Indicator>
              <Text fw={800} c="#2d2d2d" size="lg" mt="sm">{currentAgent ? currentAgent.name : AGENT.name}</Text>
              <Badge variant="light" color="blue" mt={4}>{currentAgent ? currentAgent.role : AGENT.role}</Badge>
              <Group justify="center" gap="xs" mt="sm">
                <Badge color={agentStatus === 'online' ? 'green' : 'orange'} variant="dot" size="sm">● {agentStatus === 'online' ? 'Online' : 'Away'}</Badge>
                <Text size="xs" c="dimmed">{currentAgent ? currentAgent.timezone : AGENT.timezone}</Text>
              </Group>
            </Paper>

            {/* Stats grid */}
            <SimpleGrid cols={2} spacing="sm">
              {[
                ['Total Chats', currentAgent ? currentAgent.stats.totalChats : AGENT.stats.totalChats, 'blue'],
                ['Resolved Today', currentAgent ? currentAgent.stats.resolvedToday : AGENT.stats.resolvedToday, 'green'],
                ['Avg Response', currentAgent ? currentAgent.stats.avgResponseTime : AGENT.stats.avgResponseTime, 'orange'],
                ['Rating', currentAgent ? `${currentAgent.stats.rating} ⭐` : AGENT.stats.rating, 'yellow'],
              ].map(([label, val, color]) => (
                <Paper key={label} p="sm" radius="md" style={{ background:'#ffffff', border:'1px solid #e8e5f0', textAlign:'center' }}>
                  <Text size="xl" fw={800} c={color}>{val}</Text>
                  <Text size="xs" c="dimmed">{label}</Text>
                </Paper>
              ))}
            </SimpleGrid>

            {/* Issue Monitoring */}
            <Paper p="md" radius="md" style={{ background:'#ffffff', border:'1px solid #e8e5f0' }}>
              <Group gap="xs" mb="sm">
                <IconChartBar size={16} color="#9381ff" />
                <Text size="sm" fw={600} c="#2d2d2d">Issue Monitoring</Text>
              </Group>
              <SimpleGrid cols={3} spacing="xs">
                <Paper p="sm" radius="md" style={{ background:'rgba(64,192,87,0.08)', border:'1px solid rgba(64,192,87,0.2)', textAlign:'center' }}>
                  <Text size="lg" fw={800} c="green">{issueStats.resolved}</Text>
                  <Text size="xs" c="dimmed">Solved</Text>
                </Paper>
                <Paper p="sm" radius="md" style={{ background:'rgba(255,59,59,0.08)', border:'1px solid rgba(255,59,59,0.2)', textAlign:'center' }}>
                  <Text size="lg" fw={800} c="red">{issueStats.unresolved}</Text>
                  <Text size="xs" c="dimmed">Unsolved</Text>
                </Paper>
                <Paper p="sm" radius="md" style={{ background:'rgba(255,165,0,0.08)', border:'1px solid rgba(255,165,0,0.2)', textAlign:'center' }}>
                  <Text size="lg" fw={800} c="orange">{issueStats.escalated}</Text>
                  <Text size="xs" c="dimmed">Escalated</Text>
                </Paper>
              </SimpleGrid>
              <Group justify="center" mt="sm">
                <Button 
                  variant="subtle" 
                  size="xs" 
                  color={issueStats.unresolved > 0 ? 'red' : 'gray'}
                  onClick={() => setStatusFilter('open')}
                >
                  View Open Issues ({issueStats.unresolved})
                </Button>
              </Group>
            </Paper>

            {/* SLA Monitoring */}
            <Paper p="md" radius="md" style={{ background:'#ffffff', border:'1px solid #e8e5f0' }}>
              <Group gap="xs" mb="sm">
                <IconClock size={16} color="#be4bdb" />
                <Text size="sm" fw={600} c="#2d2d2d">SLA Monitoring</Text>
                <Badge size="xs" color="gray" variant="dot">{SLA_THRESHOLD_MINUTES}m threshold</Badge>
              </Group>
              <Stack gap="xs">
                {/* SLA Compliance Ring */}
                <Group justify="center">
                  <RingProgress
                    size={100}
                    thickness={10}
                    roundCaps
                    sections={[{
                      value: slaStats.slaCompliance, 
                      color: slaStats.slaCompliance >= 80 ? 'green' : slaStats.slaCompliance >= 50 ? 'orange' : 'red'
                    }]}
                    label={
                      <Text size="lg" fw={800} ta="center" c="#2d2d2d">{slaStats.slaCompliance}%</Text>
                    }
                  />
                </Group>
                <Text size="xs" c="dimmed" ta="center">SLA Compliance Rate</Text>
                
                {/* SLA Stats */}
                <SimpleGrid cols={2} spacing="xs" mt="xs">
                  <Paper p="xs" radius="md" style={{ background:'rgba(64,192,87,0.08)', border:'1px solid rgba(64,192,87,0.2)', textAlign:'center' }}>
                    <Text size="sm" fw={700} c="green">{formatResolutionTime(slaStats.avgResolutionTime)}</Text>
                    <Text size="xs" c="dimmed">Avg Resolution</Text>
                  </Paper>
                  <Paper p="xs" radius="md" style={{ background: slaStats.openAge > SLA_THRESHOLD_MINUTES ? '#2d1f1f' : '#0d3324', border: slaStats.openAge > SLA_THRESHOLD_MINUTES ? '1px solid #5c1a1a' : '1px solid #1a5c4a', textAlign:'center' }}>
                    <Text size="sm" fw={700} c={slaStats.openAge > SLA_THRESHOLD_MINUTES ? 'red' : 'green'}>{formatResolutionTime(slaStats.openAge)}</Text>
                    <Text size="xs" c="dimmed">Avg Open Age</Text>
                  </Paper>
                </SimpleGrid>
                
                <SimpleGrid cols={2} spacing="xs">
                  <Paper p="xs" radius="md" style={{ background:'rgba(64,192,87,0.08)', border:'1px solid rgba(64,192,87,0.2)', textAlign:'center' }}>
                    <Text size="sm" fw={700} c="green">{slaStats.withinSLA}</Text>
                    <Text size="xs" c="dimmed">Within SLA</Text>
                  </Paper>
                  <Paper p="xs" radius="md" style={{ background:'rgba(255,59,59,0.08)', border:'1px solid rgba(255,59,59,0.2)', textAlign:'center' }}>
                    <Text size="sm" fw={700} c="red">{slaStats.breachedSLA}</Text>
                    <Text size="xs" c="dimmed">SLA Breached</Text>
                  </Paper>
                </SimpleGrid>
              </Stack>
            </Paper>

            {/* Satisfaction */}
            <Paper p="md" radius="md" style={{ background:'#ffffff', border:'1px solid #e8e5f0' }}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={600} c="#2d2d2d">Customer Satisfaction</Text>
                <Text size="sm" fw={700} c="violet">{currentAgent ? currentAgent.stats.satisfaction : AGENT.stats.satisfaction}%</Text>
              </Group>
              <Progress value={currentAgent ? currentAgent.stats.satisfaction : AGENT.stats.satisfaction} size="sm" radius="xl" color="violet"/>
            </Paper>

            {/* Contact info */}
            <Paper p="md" radius="md" style={{ background:'#ffffff', border:'1px solid #e8e5f0' }}>
              <Text size="sm" fw={600} c="#2d2d2d" mb="sm">Contact Information</Text>
              <Stack gap="xs">
                {[
                  ['✉', 'Email', currentAgent ? currentAgent.email : AGENT.email],
                  ['📞', 'Phone', currentAgent ? currentAgent.phone : AGENT.phone || 'Not set'],
                  ['📍', 'Location', currentAgent ? currentAgent.location : AGENT.location],
                  ['🕐', 'Timezone', currentAgent ? currentAgent.timezone : AGENT.timezone],
                  ['📅', 'Joined', currentAgent ? currentAgent.joined : AGENT.joined],
                  ['🏢', 'Department', currentAgent ? currentAgent.department : AGENT.role],
                ].map(([icon, label, value]) => (
                  <Group key={label} gap="sm" wrap="nowrap">
                    <Text size="sm">{icon}</Text>
                    <Box>
                      <Text size="xs" c="dimmed">{label}</Text>
                      <Text size="xs" c="#2d2d2d" fw={500}>{value}</Text>
                    </Box>
                  </Group>
                ))}
              </Stack>
            </Paper>

            {/* Skills */}
            <Paper p="md" radius="md" style={{ background:'#ffffff', border:'1px solid #e8e5f0' }}>
              <Text size="sm" fw={600} c="#2d2d2d" mb="sm">Specializations</Text>
              <Group gap="xs">
                {(currentAgent ? currentAgent.skills : AGENT.skills).map(skill => (
                  <Badge key={skill} variant="light" color="blue" radius="xl" size="sm">{skill}</Badge>
                ))}
              </Group>
            </Paper>

            {/* Actions */}
            <Stack gap="xs">
              <Button variant="light" color="blue" radius="xl" fullWidth onClick={handleOpenEditProfile}>✏️ Edit Profile</Button>
              <Button variant="light" color="orange" radius="xl" fullWidth onClick={handleToggleStatus}>
                {agentStatus === 'online' ? '🌙 Set Away' : '🟢 Go Online'}
              </Button>
            </Stack>
          </Stack>
        </ScrollArea>
      </Drawer>

      {/* ── Incoming Call Modal ── */}
      {incomingCall && !callStatus && (
        <Modal opened onClose={rejectCall} withCloseButton={false} centered closeOnClickOutside={false}
          styles={{content:{background:'#ffffff',border:'1px solid #e8e5f0'}}}>
          <Stack align="center" gap="md" py="lg">
            <Box style={{
              width:100, height:100, borderRadius:'50%',
              background:'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 0 0 12px rgba(245,87,108,0.15)',
              animation:'pulse 2s infinite'
            }}>
              {incomingCall.callType === 'video' ? <IconVideo size={48} color="white"/> : <IconPhone size={48} color="white"/>}
            </Box>
            <Title order={4} c="#2d2d2d" fw={700}>Incoming {incomingCall.callType === 'video' ? 'Video' : 'Audio'} Call</Title>
            <Text c="dimmed" size="sm" ta="center">
              {incomingCall.from || 'Customer'} is calling you
            </Text>
            <Group gap="xl" mt="md">
              <ActionIcon size={60} radius="xl" color="red" variant="filled"
                onClick={rejectCall}
                style={{ boxShadow:'0 4px 20px rgba(255,0,0,0.4)' }}>
                <IconPhoneOff size={28} />
              </ActionIcon>
              <ActionIcon size={60} radius="xl" color="green" variant="filled"
                onClick={acceptCall}
                style={{ boxShadow:'0 4px 20px rgba(0,255,0,0.4)' }}>
                <IconPhone size={28} />
              </ActionIcon>
            </Group>
            <Text c="dimmed" size="xs" mt="sm">
              Press accept to answer or reject to decline
            </Text>
          </Stack>
        </Modal>
      )}

      {/* ── Outgoing / Ringing Call Modal ── */}
      {!incomingCall && callStatus === 'calling' && callModal && (
        <Modal opened onClose={() => { endCall(); toast('Call cancelled','red'); }} withCloseButton={false} centered closeOnClickOutside={false}
          styles={{content:{background:'#ffffff',border:'1px solid #e8e5f0'}}}>
          <Stack align="center" gap="md" py="lg">
            <Box style={{
              width:100, height:100, borderRadius:'50%',
              background:'linear-gradient(135deg, #9381ff 0%, #b8b8ff 100%)',
              display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 0 0 12px rgba(147,129,255,0.08)',
              animation:'pulse 2s infinite'
            }}>
              {callModal === 'video' ? <IconVideo size={48} color="white"/> : <IconPhone size={48} color="white"/>}
            </Box>
            <Title order={4} c="#2d2d2d" fw={700}>Calling {selectedData?.userName || 'Customer'}...</Title>
            <Text c="dimmed" size="sm" ta="center">
              {callModal === 'video' ? 'Video' : 'Voice'} call · Ringing
            </Text>
            <Group gap="xl" mt="md">
              <ActionIcon size={60} radius="xl" color="red" variant="filled"
                onClick={() => { endCall(); toast('Call cancelled','red'); }}
                style={{ boxShadow:'0 4px 20px rgba(255,0,0,0.4)' }}>
                <IconPhoneOff size={28} />
              </ActionIcon>
            </Group>
            <Text c="dimmed" size="xs" mt="sm">
              Waiting for customer to answer…
            </Text>
          </Stack>
        </Modal>
      )}

      {/* ── Active Call Modal ── */}
      {callStatus === 'connected' && callModal && (
        <Modal opened onClose={endCall} withCloseButton={false} centered size="lg" closeOnClickOutside={false}
          styles={{content:{background:'#ffffff',border:'1px solid #e8e5f0'},body:{padding:0}}}>
          <Stack align="center" gap="md" py="lg" px="md">
            {/* Hidden audio element for remote audio playback */}
            <audio 
              ref={audioRef => {
                if (audioRef && remoteStream) {
                  audioRef.srcObject = remoteStream;
                  const playPromise = audioRef.play();
                  if (playPromise !== undefined) {
                    playPromise.catch(e => {
                      console.log('[WebRTC Admin] Audio play error:', e.message);
                      // Try again after a short delay (browser autoplay policy)
                      setTimeout(() => {
                        if (audioRef.srcObject) {
                          audioRef.play().catch(e2 => console.log('[WebRTC Admin] Retry audio play error:', e2.message));
                        }
                      }, 1000);
                    });
                  }
                }
              }} 
              autoPlay 
              playsInline
            />
            
            {/* Video call layout */}
            {callModal === 'video' ? (
              <Box style={{position:'relative',borderRadius:12,overflow:'hidden',background:'#000',width:'100%',aspectRatio:'16/9',minHeight:260}}>
                {remoteStream ? (
                  <video 
                    ref={v => { if (v && remoteStream) { v.srcObject = remoteStream; } }} 
                    autoPlay playsInline 
                    style={{width:'100%',height:'100%',objectFit:'cover'}} 
                  />
                ) : (
                  <Stack align="center" justify="center" style={{position:'absolute',inset:0}}>
                    <Avatar size={70} color={avatarColor(selectedData?.userName)} radius={70}>{getInitial(selectedData?.userName)}</Avatar>
                    <Text c="dimmed" size="sm">Connecting video…</Text>
                  </Stack>
                )}
                {/* Local preview pip */}
                {localStream && (
                  <Box style={{position:'absolute',bottom:12,right:12,width:130,aspectRatio:'16/9',borderRadius:10,overflow:'hidden',border:'2px solid rgba(255,255,255,0.6)',boxShadow:'0 2px 12px rgba(0,0,0,0.5)'}}>
                    <video 
                      ref={v => { if (v && localStream) { v.srcObject = localStream; } }} 
                      autoPlay playsInline muted 
                      style={{width:'100%',height:'100%',objectFit:'cover',transform:'scaleX(-1)'}} 
                    />
                  </Box>
                )}
              </Box>
            ) : (
              /* Audio call layout */
              <>
                <Box style={{
                  width:100, height:100, borderRadius:'50%',
                  background:'linear-gradient(135deg, #00c853 0%, #00e676 100%)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  boxShadow:'0 0 0 12px rgba(0,200,83,0.15)',
                }}>
                  <IconPhone size={48} color="white"/>
                </Box>
              </>
            )}

            {/* Call info */}
            <Text fw={700} c="#2d2d2d" size="lg">{selectedData?.userName || 'Customer'}</Text>
            <Group gap="sm" align="center">
              <Badge color="green" variant="dot" size="lg">● Connected</Badge>
              <Text fw={600} c="#2d2d2d" size="lg" style={{ fontFamily: 'monospace', background: '#ede9ff', padding: '4px 12px', borderRadius: '8px' }}>
                {formatCallDuration(callDuration)}
              </Text>
            </Group>

            {/* Call controls */}
            <Group gap="lg" mt="sm">
              <Tooltip label="End call">
                <ActionIcon size={60} radius="xl" color="red" variant="filled"
                  onClick={() => endCall()}
                  style={{ boxShadow:'0 4px 20px rgba(255,0,0,0.4)' }}>
                  <IconPhoneOff size={28} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* Clear History Confirmation Modal */}
      <Modal 
        opened={clearHistoryModalOpen} 
        onClose={() => setClearHistoryModalOpen(false)}
        title="Remove All History"
        centered
      >
        <Text size="sm" mb="lg">
          Are you sure you want to remove all chat history? This action cannot be undone.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={() => setClearHistoryModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={confirmClearHistory}>
            Remove All
          </Button>
        </Group>
      </Modal>

      {/* ── Voucher Search Modal ── */}
      <VoucherSearchModal
        opened={voucherSidebarOpen}
        onClose={() => setVoucherSidebarOpen(false)}
      />

      {/* ── Latest Voucher Modal ── */}
      <VoucherLatestModal
        opened={latestVoucherOpened}
        onClose={() => setLatestVoucherOpened(false)}
      />

      {/* ── Transfer to Manager Modal ── */}
      <Modal
        opened={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        title="Transfer to Manager"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select a manager to transfer this conversation to:
          </Text>
          {managers.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No managers available
            </Text>
          ) : (
            <Stack gap="xs">
              {managers.map((manager) => (
                <Paper 
                  key={manager._id} 
                  p="sm" 
                  style={{ 
                    background: selectedManager?._id === manager._id ? '#ede9ff' : '#ffffff',
                    border: '1px solid #e8e5f0',
                    cursor: 'pointer',
                    borderRadius: '8px'
                  }}
                  onClick={() => setSelectedManager(manager)}
                >
                  <Group justify="space-between">
                    <Box>
                      <Text size="sm" fw={600} c="#2d2d2d">{manager.fullName || manager.username}</Text>
                      <Text size="xs" c="dimmed">{manager.email}</Text>
                    </Box>
                    <Badge color="blue" variant="light">{manager.role}</Badge>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" color="gray" onClick={() => setTransferModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              color="blue" 
              onClick={() => handleTransferToManager(selectedManager)}
              disabled={!selectedManager}
            >
              Transfer
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Notes Modal ── */}
      <Modal
        opened={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        title={`Notes for ${selectedData?.userName}`}
        centered
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Add or edit notes/summary for this conversation:
          </Text>
          <Textarea
            placeholder="Enter your notes here... e.g., Issue resolved by updating customer email, customer agreed to check settings..."
            minRows={6}
            maxRows={10}
            value={editingNotes}
            onChange={(e) => setEditingNotes(e.currentTarget.value)}
            style={{ fontFamily: 'monospace', fontSize: '13px' }}
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => setNotesModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={() => {
                socket.emit('admin:save_notes', { roomId: selectedRoom, notes: editingNotes });
                setNotesModalOpen(false);
                toast('✅ Notes saved', 'green');
              }}
            >
              Save Notes
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Escalation Modal ── */}
      <Modal
        opened={escalateModalOpen}
        onClose={() => setEscalateModalOpen(false)}
        title="Escalate to Manager"
        centered
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Provide a summary or note for the manager before escalating this conversation. This is required so the manager understands the issue.
          </Text>
          <Textarea
            label="Escalation Summary / Notes"
            placeholder="Describe the issue, what you've tried, and why this needs manager attention..."
            minRows={5}
            maxRows={10}
            value={escalateNotes}
            onChange={(e) => { setEscalateNotes(e.currentTarget.value); setEscalateError(''); }}
            error={escalateError}
            withAsterisk
            style={{ fontFamily: 'monospace', fontSize: '13px' }}
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => setEscalateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="orange"
              leftSection={<IconAlertTriangle size={16}/>}
              onClick={() => {
                if (!escalateNotes.trim()) {
                  setEscalateError('Please provide a summary before escalating');
                  return;
                }
                if (escalateNotes.trim().length < 10) {
                  setEscalateError('Summary is too short. Please provide more detail.');
                  return;
                }
                handleEscalateToMonitor(escalateNotes.trim());
                setEscalateModalOpen(false);
              }}
            >
              Escalate
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal
        opened={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        title="Edit Profile"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Full Name"
            value={editProfileForm.fullName}
            onChange={(e) => setEditProfileForm({ ...editProfileForm, fullName: e.target.value })}
            placeholder="Enter your full name"
          />
          <TextInput
            label="Phone"
            value={editProfileForm.phone}
            onChange={(e) => setEditProfileForm({ ...editProfileForm, phone: e.target.value })}
            placeholder="Enter phone number"
          />
          <TextInput
            label="Department"
            value={editProfileForm.department}
            onChange={(e) => setEditProfileForm({ ...editProfileForm, department: e.target.value })}
            placeholder="Enter department"
          />
          <Group justify="flex-end" gap="sm" mt="md">
            <Button variant="subtle" onClick={() => setEditProfileOpen(false)}>
              Cancel
            </Button>
            <Button color="blue" onClick={handleEditProfile}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
